import React, { useState, useEffect, useRef } from 'react';
import { Field, ErrorMessage } from 'formik';
import DOMPurify from 'dompurify';
import { getFieldName } from '../../utils/fieldNameMapper';
import { US_STATES, DEFAULT_STATE } from '../../utils/usStates';
import axios from 'axios';
import { environment } from '../../env/environment';
import MultiStepDatePicker from './MultiStepDatePicker';
import DatePicker from './DeferredCalendar';
import DocumentUpload from './DocumentUpload';
import { compressBase64Image } from '../../services/imageCompressor';
import { useTenant } from '../../context/TenantContext';
import logger from '../../utils/logger';
import { hasNewImagePreview } from '../../utils/configuredImage';


// Display as "Zip Code" even when the API says "Postal Code", keeping the
// original casing so placeholders like "enter your postal code" still read right
const toZipCodeWording = (text) =>
  typeof text === 'string'
    ? text.replace(/postal\s*code/gi, (m) =>
        m === m.toLowerCase() ? 'zip code' : m === m.toUpperCase() ? 'ZIP CODE' : 'Zip Code')
    : text;

const AccordionSection = ({ section, sectionIndex, sectionFieldName, formikValues }) => {
  const hasCheckedCheckbox = section.checkboxes?.some((checkbox, checkboxIndex) => {
    const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
    return formikValues && formikValues[checkboxFieldName];
  });

  const [isExpanded, setIsExpanded] = useState(hasCheckedCheckbox || false);


  useEffect(() => {
    if (hasCheckedCheckbox && !isExpanded) {
      setIsExpanded(true);
    }
  }, [hasCheckedCheckbox]);

  return (
    <div className={`accordion-item ${isExpanded ? 'active' : ''}`}>
      {/* Accordion Header */}
      <div
        className="accordion-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >

        <span className="accordion-title">{section.title}</span>
        {/* Right side checkbox - checked when accordion is expanded */}
        <div
          className="accordion-expand-checkbox-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="accordion-expand-checkbox"
            checked={isExpanded}
            onChange={(e) => {
              setIsExpanded(e.target.checked);
            }}
          />
        </div>
      </div>

      {/* Accordion Content - Checkboxes in 3 column grid */}
      {isExpanded && (
        <div className="accordion-content">
          <div className="accordion-checkboxes-grid">
            {section.checkboxes?.map((checkbox, checkboxIndex) => {
              const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;

              return (
                <Field key={checkbox.id || checkboxIndex} name={checkboxFieldName}>
                  {({ field: cbField, form }) => (
                    <React.Fragment>
                      <div className="accordion-checkbox-item">
                        <label className={`accordion-checkbox-label ${cbField.value ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            className="accordion-checkbox-input"
                            checked={cbField.value || false}
                            onChange={(e) => {
                              form.setFieldValue(checkboxFieldName, e.target.checked);
                            }}
                          />
                          <span className="accordion-checkbox-text">{checkbox.label}</span>
                        </label>
                      </div>

                      {/* Nested inputs - show only when checkbox is checked */}
                      {cbField.value && checkbox.nestedInputs && checkbox.nestedInputs.length > 0 && (
                        <div className="accordion-nested-inputs" style={{ gridColumn: '1 / -1' }}>
                          {checkbox.nestedInputs.map((nestedInput, inputIndex) => {
                            const nestedFieldName = `${checkboxFieldName}_input_${inputIndex}`;

                            return (
                              <div key={nestedInput.id || inputIndex} className="accordion-nested-input-item">
                                <label className="accordion-nested-label">
                                  {nestedInput.label}
                                  {nestedInput.required && <span className="required">*</span>}
                                </label>
                                <Field name={nestedFieldName}>
                                  {({ field: inputField }) => {
                                    if (nestedInput.type === 'textarea') {
                                      return (
                                        <textarea
                                          {...inputField}
                                          placeholder={nestedInput.placeholder}
                                          className="accordion-nested-textarea"
                                          rows={3}
                                        />
                                      );
                                    }
                                    if (nestedInput.type === 'number') {
                                      const currentVal = parseInt(inputField.value) || 0;
                                      return (
                                        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                          <input
                                            type="number"
                                            {...inputField}
                                            placeholder={nestedInput.placeholder}
                                            className="accordion-nested-input"
                                            style={{ paddingRight: '35px' }}
                                          />
                                          <div className="number-spinner-buttons">
                                            <button
                                              type="button"
                                              onClick={() => form.setFieldValue(nestedFieldName, currentVal + 1)}
                                              className="spinner-btn spinner-up"
                                            >
                                              ▲
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => form.setFieldValue(nestedFieldName, currentVal - 1)}
                                              className="spinner-btn spinner-down"
                                            >
                                              ▼
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <input
                                        type={nestedInput.type || 'text'}
                                        {...inputField}
                                        placeholder={nestedInput.placeholder}
                                        className="accordion-nested-input"
                                      />
                                    );
                                  }}
                                </Field>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </Field>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const DynamicStepComponent = ({
  step,
  moduleName,
  moduleFields,
  moduleData,
  values,
  errors,
  touched,
  setCanvasGender,
  errorDisplay,
  setPainLevels,
}) => {
  // Get tenant for dynamic branding
  const { tenant } = useTenant();
  const brandName = tenant?.brandName || 'Our Practice';

  // Search functionality states
  const [searchResults, setSearchResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  const [showSearchResults, setShowSearchResults] = useState({});
  const [searchQueries, setSearchQueries] = useState({});
  const searchTimeouts = useRef({});
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMedOpen, setIsMedOpen] = useState(false);
  const [medSearchTerm, setMedSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 467);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 467);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fieldMappingConfig = {
    name: ['name', 'fullName', 'full_name', 'title', 'displayName', 'userName', 'pharmacy_name', 'shop_name'],
    address: ['address', 'location', 'street', 'fullAddress', 'full_address', 'shop_address'],
    phone: ['phone', 'phoneNumber', 'phone_number', 'mobile', 'contact', 'tel', 'contact_number'],
    city: ['city', 'town', 'municipality'],
    state: ['state', 'province', 'region'],
    zipcode: ['zipcode', 'zip', 'postalCode', 'postal_code', 'pincode'],
    country: ['country', 'nation'],
  };

  const detectFieldType = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase();

    for (const [fieldType, possibleKeys] of Object.entries(fieldMappingConfig)) {
      if (possibleKeys.some(key => lowerFieldName.includes(key))) {
        return fieldType;
      }
    }
    return null;
  };

  const getBestMatchingValue = (jsonObject, targetFieldType) => {
    if (!jsonObject || typeof jsonObject !== 'object') return null;

    const possibleKeys = fieldMappingConfig[targetFieldType] || [];

    for (const key of possibleKeys) {
      if (jsonObject[key]) {
        return jsonObject[key];
      }
    }

    for (const key of possibleKeys) {
      const foundKey = Object.keys(jsonObject).find(
        objKey => objKey.toLowerCase() === key.toLowerCase()
      );
      if (foundKey && jsonObject[foundKey]) {
        return jsonObject[foundKey];
      }
    }

    return null;
  };

  const autoPopulateRelatedFields = (selectedResult, currentFieldName, formInstance, moduleFields) => {
    if (typeof selectedResult !== 'object' || !selectedResult || !formInstance) return;

    const allFormFields = moduleFields.map((field, index) => ({
      fieldName: getFieldName(field, index),
      fieldType: detectFieldType(getFieldName(field, index)),
      originalField: field
    }));

    allFormFields.forEach(({ fieldName, fieldType }) => {
      if (fieldName === currentFieldName) return;

      if (fieldType) {
        const matchingValue = getBestMatchingValue(selectedResult, fieldType);
        if (matchingValue) {
          formInstance.setFieldValue(fieldName, matchingValue);
        }
      }
    });
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    );
  };

  const performSearch = async (fieldName, query, fieldIndex) => {
    if (!query || query.length < 1) {
      setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
      setShowSearchResults(prev => ({ ...prev, [fieldName]: false }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await axios.get(
        `${environment.baseUrl}/api/modules/searchData/${encodeURIComponent(moduleName)}/${fieldIndex}`,
        {
          params: {
            query: query,
            maxResults: 10
          }
        }
      );

      const results = response.data.results || [];
      setSearchResults(prev => ({
        ...prev,
        [fieldName]: results
      }));
      setShowSearchResults(prev => ({ ...prev, [fieldName]: true }));

    } catch (error) {
      logger.error('Search error:', error);

      if (error.response?.status === 404) {
        logger.warn(`No search data found for ${moduleName} field ${fieldIndex}`);
        setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
      } else {
        logger.error('Search API error:', error.response?.data || error.message);
        setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
      }

      setShowSearchResults(prev => ({ ...prev, [fieldName]: false }));
    } finally {
      setSearchLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSearchInput = (fieldName, value, fieldIndex) => {
    setSearchQueries(prev => ({ ...prev, [fieldName]: value }));

    if (searchTimeouts.current[fieldName]) {
      clearTimeout(searchTimeouts.current[fieldName]);
    }

    searchTimeouts.current[fieldName] = setTimeout(() => {
      performSearch(fieldName, value, fieldIndex);
    }, 150);
  };

  const handleSearchResultSelectSmart = (fieldName, result, formInstance) => {
    if (!formInstance) {
      logger.error('Form instance not available');
      return;
    }

    let resultValue;

    if (typeof result === 'string') {
      resultValue = result;
    } else if (typeof result === 'object' && result !== null) {
      const fieldType = detectFieldType(fieldName);

      if (fieldType) {
        resultValue = getBestMatchingValue(result, fieldType);
      }

      if (!resultValue) {
        if (result.name) {
          resultValue = result.name;
        } else if (result.title) {
          resultValue = result.title;
        } else if (result.label) {
          resultValue = result.label;
        } else {
          const firstStringValue = Object.values(result).find(val => typeof val === 'string');
          resultValue = firstStringValue || JSON.stringify(result);
        }
      }

      autoPopulateRelatedFields(result, fieldName, formInstance, moduleFields);
    } else {
      resultValue = String(result);
    }

    formInstance.setFieldValue(fieldName, resultValue);
    setShowSearchResults(prev => ({ ...prev, [fieldName]: false }));
    setSearchQueries(prev => ({ ...prev, [fieldName]: resultValue }));
    setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
  };

  // Desktop dropdown style result
  const renderSearchResultDropdown = (result, resultIndex, fieldName, formInstance) => {
    return (
      <div
        key={resultIndex}
        className="search-result-item"
        onClick={() => handleSearchResultSelectSmart(fieldName, result, formInstance)}
        style={{
          padding: '12px 15px',
          cursor: 'pointer',
          borderBottom: resultIndex < searchResults[fieldName].length - 1 ? '1px solid #eee' : 'none',
          backgroundColor: 'white',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
      >
        {typeof result === 'string' ? (
          <div style={{ fontSize: '14px', color: '#333' }}>
            {highlightSearchTerm(result, searchQueries[fieldName] || '')}
          </div>
        ) : (
          <div>
            {result.name && (
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                {highlightSearchTerm(result.name, searchQueries[fieldName] || '')}
              </div>
            )}
            {result.address && (
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                {highlightSearchTerm(result.address, searchQueries[fieldName] || '')}
              </div>
            )}
            {result.phone && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {highlightSearchTerm(result.phone, searchQueries[fieldName] || '')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Mobile card style result
  const renderSearchResultCard = (result, resultIndex, fieldName, formInstance) => {
    return (
      <div
        key={resultIndex}
        className="search-result-card"
        onClick={() => handleSearchResultSelectSmart(fieldName, result, formInstance)}
        style={{
          padding: '16px',
          cursor: 'pointer',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          marginBottom: '12px',
          transition: 'all 0.2s ease'
        }}
      >
        {typeof result === 'string' ? (
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
            {highlightSearchTerm(result, searchQueries[fieldName] || '')}
          </div>
        ) : (
          <div>
            {result.name && (
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px', lineHeight: '1.3' }}>
                {highlightSearchTerm(result.name, searchQueries[fieldName] || '')}
              </div>
            )}
            {result.company && (
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {highlightSearchTerm(result.company, searchQueries[fieldName] || '')}
              </div>
            )}
            {result.address && (
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>
                {highlightSearchTerm(result.address, searchQueries[fieldName] || '')}
              </div>
            )}
            {(result.city || result.state || result.zipcode) && (
              <div style={{ fontSize: '13px', color: '#888' }}>
                {[result.city, result.state, result.zipcode].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-field-container')) {
        setShowSearchResults({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if this is the Authorized Contacts module (for conditional field styling)
  const isAuthorizedContacts = moduleName &&
    (moduleName.toLowerCase().includes('authorized') && moduleName.toLowerCase().includes('contact')) ||
    (moduleData?.display_name?.toLowerCase().includes('authorized') && moduleData?.display_name?.toLowerCase().includes('contact'));

  const renderField = (field, index) => {
    if (!field || typeof field !== 'object') {
      logger.warn('Invalid field object:', field);
      return null;
    }

    const {
      // name: field_name_from_api,
      type: field_type,
      labelName: field_label,
      label: field_label_alt,
      options: field_options,
      required: is_required,
      placeholder: rawFieldPlaceholder,
      minValue,
      maxValue,
      defaultValue,
      showLabels,
      rangeLabels,
      step: stepValue,
      uploadedImage,
      termsText,
      searchPlaceholder,
      dependsOn, // Field dependency configuration
    } = field;

    const finalFieldLabel = toZipCodeWording(field_label || field_label_alt);
    const field_placeholder = toZipCodeWording(rawFieldPlaceholder);
    const field_name = getFieldName(field, index);

    if (!field_name || !field_type) {
      logger.warn('Field missing required properties:', field);
      return null;
    }

    // Check field dependency - hide field if dependency not met
    if (dependsOn) {
      const { field: dependentFieldName, value: dependentValue, values: dependentValues } = dependsOn;
      const currentDependentValue = values[dependentFieldName];

      // Support both single value and array of values
      const allowedValues = dependentValues || (dependentValue ? [dependentValue] : []);

      if (allowedValues.length > 0) {
        const isDependencyMet = allowedValues.some(val =>
          currentDependentValue === val ||
          currentDependentValue?.startsWith(`${val} -`) ||
          currentDependentValue?.includes(val)
        );

        if (!isDependencyMet) {
          return null; // Don't render field if dependency not met
        }
      }
    }
    const fieldKey = `${field_name}_${index}`;

    try {
      switch (field_type) {

        case 'text':
        case 'email': {
          // For half-width fields (<=50%), use calc to account for gap
          const isHalfWidth = !isMobile && field.fieldWidth && field.fieldWidth <= 50;
          const textWidth = isMobile ? '100%' : (isHalfWidth ? 'calc(50% - 10px)' : `${field.fieldWidth || 100}%`);

          // ✅ Regular text/email input (State is now handled by separate 'state' case)
          return (
            <div key={fieldKey} className="form-group" style={{ width: textWidth, flex: isHalfWidth ? '0 0 auto' : undefined }}>
              {!field.hideLabel && (
                <label htmlFor={field_name} className="label-txt">
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field
                type={field_type}
                name={field_name}
                id={field_name}
                placeholder={field_placeholder || ''}
                className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}

              />
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
        }

        // ✅ Composite Address field type (configured from Admin panel)
        // Includes: Street Address, Suite/Apt, City, State dropdown (US only), Country (fixed US)
        case 'address': {
          const addressConfig = field.addressConfig || {
            streetAddress: { label: 'Street Address', placeholder: 'Street Address', required: true, enabled: true },
            suiteApt: { label: 'Suite No/Apt No.', placeholder: 'Suite No/Apt No.', required: false, enabled: true },
            city: { label: 'City', placeholder: 'City', required: true, enabled: true },
            state: { label: 'State', placeholder: 'State', required: true, enabled: true, defaultValue: DEFAULT_STATE },
            country: { label: 'Country', placeholder: 'country', value: 'US', disabled: true, enabled: true }
          };

          // Check enabled status for each subfield
          const showStreet = addressConfig.streetAddress?.enabled !== false;
          const showSuite = addressConfig.suiteApt?.enabled !== false;
          const showCity = addressConfig.city?.enabled !== false;
          const showState = addressConfig.state?.enabled !== false;
          const showCountry = addressConfig.country?.enabled !== false;

          return (
            <div key={fieldKey} className="address-field-container" style={{ width: '100%' }}>
              {/* Street Address - Full Width */}
              {showStreet && (
                <div className="form-group" style={{ width: '100%', marginBottom: '15px' }}>
                  <label htmlFor={`${field_name}_street`} className="label-txt">
                    {addressConfig.streetAddress?.label || 'Street Address'}
                    {addressConfig.streetAddress?.required && <span className="required">*</span>}
                  </label>
                  <Field
                    type="text"
                    name={`${field_name}_street`}
                    id={`${field_name}_street`}
                    placeholder={addressConfig.streetAddress?.placeholder || 'Street Address'}
                    className={`form-control ${errors[`${field_name}_street`] && touched[`${field_name}_street`] ? 'error' : ''}`}
                  />
                  <ErrorMessage name={`${field_name}_street`} component="div" className="error-message" />
                </div>
              )}

              {/* Suite/Apt + City - Half Width Each */}
              {(showSuite || showCity) && (
                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  {showSuite && (
                    <div className="form-group" style={{ width: isMobile ? '100%' : 'calc(50% - 10px)' }}>
                      <label htmlFor={`${field_name}_suite`} className="label-txt">
                        {addressConfig.suiteApt?.label || 'Suite No/Apt No.'}
                        {addressConfig.suiteApt?.required && <span className="required">*</span>}
                      </label>
                      <Field
                        type="text"
                        name={`${field_name}_suite`}
                        id={`${field_name}_suite`}
                        placeholder={addressConfig.suiteApt?.placeholder || 'Suite No/Apt No.'}
                        className={`form-control ${errors[`${field_name}_suite`] && touched[`${field_name}_suite`] ? 'error' : ''}`}
                      />
                      <ErrorMessage name={`${field_name}_suite`} component="div" className="error-message" />
                    </div>
                  )}

                  {showCity && (
                    <div className="form-group" style={{ width: isMobile ? '100%' : (showSuite ? 'calc(50% - 10px)' : '100%') }}>
                      <label htmlFor={`${field_name}_city`} className="label-txt">
                        {addressConfig.city?.label || 'City'}
                        {addressConfig.city?.required && <span className="required">*</span>}
                      </label>
                      <Field
                        type="text"
                        name={`${field_name}_city`}
                        id={`${field_name}_city`}
                        placeholder={addressConfig.city?.placeholder || 'City'}
                        className={`form-control ${errors[`${field_name}_city`] && touched[`${field_name}_city`] ? 'error' : ''}`}
                      />
                      <ErrorMessage name={`${field_name}_city`} component="div" className="error-message" />
                    </div>
                  )}
                </div>
              )}

              {/* State + Country - Half Width Each */}
              {(showState || showCountry) && (
                <div style={{ display: 'flex', gap: '20px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  {showState && (
                    <div className="form-group" style={{ width: isMobile ? '100%' : (showCountry ? 'calc(50% - 10px)' : '100%') }}>
                      <label htmlFor={`${field_name}_state`} className="label-txt">
                        {addressConfig.state?.label || 'State'}
                        {addressConfig.state?.required && <span className="required">*</span>}
                      </label>
                      <Field
                        as="select"
                        name={`${field_name}_state`}
                        id={`${field_name}_state`}
                        className={`form-control ${errors[`${field_name}_state`] && touched[`${field_name}_state`] ? 'error' : ''}`}
                      >
                        {/* US_STATES from centralized utility - Oklahoma first */}
                        {US_STATES.map((state) => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name={`${field_name}_state`} component="div" className="error-message" />
                    </div>
                  )}

                  {showCountry && (
                    <div className="form-group" style={{ width: isMobile ? '100%' : (showState ? 'calc(50% - 10px)' : '100%') }}>
                      <label htmlFor={`${field_name}_country`} className="label-txt">
                        {addressConfig.country?.label || 'Country'}
                      </label>
                      <Field
                        type="text"
                        name={`${field_name}_country`}
                        id={`${field_name}_country`}
                        value="US"
                        disabled={true}
                        placeholder={addressConfig.country?.placeholder || 'country'}
                        className="form-control"
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        case 'phone':
          return (
            <div key={fieldKey} className="form-group">
              {!field.hideLabel && (
                <label htmlFor={field_name} className="label-txt">
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                <span style={{
                  display: 'flex', alignItems: 'center',
                  padding: '0 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
                  borderRight: 'none', borderRadius: '8px 0 0 8px', fontSize: '14px',
                  color: '#374151', fontWeight: 500, whiteSpace: 'nowrap',
                  alignSelf: 'stretch',
                }}>+1</span>
                <Field
                  type="tel"
                  name={field_name}
                  id={field_name}
                  placeholder={field_placeholder || 'Enter 10-digit number'}
                  maxLength="10"
                  pattern="[0-9]*"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  }}
                  className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
                  style={{ borderRadius: '0 8px 8px 0' }}
                />
              </div>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

        case 'search':
          return (
            <div key={fieldKey} className="form-group search-form-group" style={{ width: '100%' }}>
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field name={field_name}>
                {({ field, form }) => (
                  <div className="search-field-container" style={{ position: 'relative' }}>
                    <input
                      type="search"
                      id={field_name}
                      value={searchQueries[field_name] || field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        form.setFieldValue(field_name, value);
                        handleSearchInput(field_name, value, index);
                      }}
                      placeholder={searchPlaceholder || field_placeholder || 'Type to search...'}
                      className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
                      autoComplete="off"
                    />

                    {/* Loading indicator */}
                    {searchLoading[field_name] && (
                      <div className="search-loading" style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        Searching...
                      </div>
                    )}

                    {/* Desktop: Dropdown results */}
                    {!isMobile && showSearchResults[field_name] && searchResults[field_name] && searchResults[field_name].length > 0 && (
                      <div className="search-results-dropdown" style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        right: '0',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderTop: 'none',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        borderRadius: '0 0 8px 8px'
                      }}>
                        {searchResults[field_name].map((result, resultIndex) =>
                          renderSearchResultDropdown(result, resultIndex, field_name, form)
                        )}
                        <div style={{
                          padding: '8px 15px',
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid #eee',
                          fontSize: '11px',
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          Tap to select and auto-fill fields
                        </div>
                      </div>
                    )}

                    {/* No results message - both mobile and desktop */}
                    {showSearchResults[field_name] &&
                      searchResults[field_name] &&
                      searchResults[field_name].length === 0 &&
                      searchQueries[field_name] &&
                      searchQueries[field_name].length >= 1 &&
                      !searchLoading[field_name] && (
                        <div className="search-no-results" style={{
                          position: isMobile ? 'relative' : 'absolute',
                          top: isMobile ? 'auto' : '100%',
                          left: '0',
                          right: '0',
                          backgroundColor: '#fff9e6',
                          border: '1px solid #ffe0b2',
                          borderTop: isMobile ? '1px solid #ffe0b2' : 'none',
                          padding: '12px 15px',
                          fontSize: '13px',
                          color: '#856404',
                          zIndex: 1000,
                          textAlign: 'center',
                          borderRadius: isMobile ? '8px' : '0 0 8px 8px',
                          marginTop: isMobile ? '12px' : '0'
                        }}>
                          No results found for "{searchQueries[field_name]}"
                        </div>
                      )}
                  </div>
                )}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />

              {/* Mobile: Card results below input */}
              {isMobile && (
                <Field name={field_name}>
                  {({ form }) => (
                    showSearchResults[field_name] && searchResults[field_name] && searchResults[field_name].length > 0 && (
                      <div className="search-results-cards" style={{ marginTop: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '12px',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          Tap to select and auto-fill fields
                        </div>
                        {searchResults[field_name].map((result, resultIndex) =>
                          renderSearchResultCard(result, resultIndex, field_name, form)
                        )}
                      </div>
                    )
                  )}
                </Field>
              )}
            </div>
          );

        case 'textarea':
          return (
            <div key={fieldKey} className="form-group" style={{ width: isMobile ? '100%' : `${field.fieldWidth}%` }}>
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field
                as="textarea"
                name={field_name}
                id={field_name}
                placeholder={field_placeholder || ''}
                className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
                rows="4"
              />
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

        case 'code':
          return (
            <div key={fieldKey} className="code-preview-container form-group">
              <iframe
                title={`preview-${fieldKey}`}
                style={{ width: "100%", height: "300px", border: "1px solid #ccc", borderRadius: "8px" }}
                srcDoc={`
          <html>
            <head>
              <style>
                ${field.code?.css || ""}
              </style>
            </head>
            <body>
              ${field.code?.html || ""}
              <script>
                ${field.code?.js || ""}
              </script>
            </body>
          </html>
        `}
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          );

        case 'select':
          return (
            <div key={fieldKey} className="form-group">
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {field?.csvHeaders?.[0] || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}

              {/* Category Selection (Single Select) - Always present */}
              <Field name={`${field_name}_category`}>
                {({ field: categoryFormikField, form }) => (
                  <select
                    {...categoryFormikField}
                    id={`${field_name}_category`}
                    className={`form-control medication-select ${errors[`${field_name}_category`] && touched[`${field_name}_category`] ? 'error' : ''}`}
                    onChange={(e) => {
                      categoryFormikField.onChange(e);
                      form.setFieldValue(`${field_name}_medicine`, []);
                      form.setFieldValue(field_name, []);
                      form.setFieldValue(field_name, '');
                    }}
                  >
                    <option value="">Select {field?.csvHeaders?.[0] || 'Category'}</option>
                    {field?.categories?.map((category, categoryIndex) => (
                      <option key={`${fieldKey}_category_${categoryIndex}`} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <ErrorMessage name={`${field_name}_category`} component="div" className="error-message" />

              {/* Dynamic Second Field based on CSV mode */}
              <Field name={`${field_name}_category`}>
                {({ field: categoryField }) => {
                  const selectedCategory = categoryField.value;
                  const medicines = selectedCategory && field?.medicineData?.[selectedCategory];
                  const csvMode = field?.csvMode || (field?.csvHeaders?.length === 2 ? '2-column' : '3-column');

                  if (!selectedCategory || !medicines) return null;

                  // For 2-column CSV: Show final selection (multiple)
                  if (csvMode === '2-column') {
                    return (
                      <div style={{ marginTop: '15px' }}>
                        <Field name={field_name}>
                          {({ field: optionsField, form }) => {
                            let selectedValues = [];
                            if (Array.isArray(optionsField.value)) {
                              selectedValues = optionsField.value;
                            } else if (typeof optionsField.value === 'string' && optionsField.value.includes(' - ')) {
                              const parts = optionsField.value.split(' - ');
                              if (parts.length === 2) {
                                selectedValues = parts[1].split(' , ').map(s => s.trim());
                              }
                            }

                            const availableOptions = medicines.options || [];
                            const filteredOptions = availableOptions.filter(option =>
                              String(option).toLowerCase().includes(searchTerm.toLowerCase())
                            );

                            const updateBackendValue = (newValues) => {
                              const category = form.values[`${field_name}_category`];
                              if (category && newValues.length > 0) {
                                const optionsStr = newValues.join(' , ');
                                const formattedValue = `${category} - ${optionsStr}`;
                                form.setFieldValue(field_name, formattedValue);
                              } else {
                                form.setFieldValue(field_name, newValues);
                              }
                            };

                            const handleOptionSelect = (optionValue) => {
                              const newValues = selectedValues.includes(optionValue)
                                ? selectedValues.filter(val => val !== optionValue)
                                : [...selectedValues, optionValue];
                              updateBackendValue(newValues);
                            };

                            const removeChip = (optionValue) => {
                              const newValues = selectedValues.filter(val => val !== optionValue);
                              updateBackendValue(newValues);
                            };

                            return (
                              <div style={{ position: 'relative' }}>
                                <label className='label-txt'>
                                  {field?.csvHeaders?.[1] || 'Options'} (Multiple)
                                </label>

                                {/* Selected Option Chips */}
                                {selectedValues.length > 0 && (
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '10px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f9f9f9',
                                    minHeight: '40px',
                                    alignItems: 'center'
                                  }}>
                                    {selectedValues.map((value, chipIndex) => (
                                      <div
                                        key={`chip_${chipIndex}`}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          backgroundColor: '#e3f2fd',
                                          color: '#1976d2',
                                          padding: '4px 8px',
                                          borderRadius: '16px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          border: '1px solid #bbdefb'
                                        }}
                                      >
                                        {value}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeChip(value);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            marginLeft: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            color: '#1976d2',
                                            padding: '0',
                                            lineHeight: '1'
                                          }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Options Search Input */}
                                <div
                                  style={{ position: 'relative' }}
                                  onClick={() => setIsOpen(!isOpen)}
                                >
                                  <input
                                    type="text"
                                    placeholder={`Search ${field?.csvHeaders?.[1] || 'options'}...`}
                                    value={searchTerm}
                                    onChange={(e) => {
                                      setSearchTerm(e.target.value);
                                      setIsOpen(true);
                                    }}
                                    onFocus={() => setIsOpen(true)}
                                    className="form-control"
                                    style={{ paddingRight: '30px', cursor: 'pointer' }}
                                    readOnly
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      color: '#666'
                                    }}
                                  >
                                    {isOpen ? '▲' : '▼'}
                                  </div>
                                </div>

                                {/* Options Dropdown */}
                                {isOpen && (
                                  <>
                                    <div style={{
                                      position: 'absolute',
                                      top: 'auto',
                                      left: '0',
                                      right: '0',
                                      backgroundColor: 'white',
                                      border: '1px solid #ddd',
                                      borderTop: 'none',
                                      maxHeight: '200px',
                                      overflowY: 'auto',
                                      zIndex: 1000,
                                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                      borderRadius: '0 0 4px 4px'
                                    }}>
                                      {filteredOptions.map((option, optionIndex) => {
                                        const optionValue = String(option);
                                        const isSelected = selectedValues.includes(optionValue);

                                        return (
                                          <div
                                            key={`option_${optionIndex}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOptionSelect(optionValue);
                                            }}
                                            style={{
                                              padding: '10px 15px',
                                              cursor: 'pointer',
                                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                              borderBottom: optionIndex < filteredOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                              display: 'flex',
                                              alignItems: 'center',
                                              fontSize: '14px',
                                              transition: 'background-color 0.2s ease'
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              readOnly
                                              style={{
                                                marginRight: '10px',
                                                cursor: 'pointer'
                                              }}
                                            />
                                            {option}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div
                                      style={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 999
                                      }}
                                      onClick={() => setIsOpen(false)}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          }}
                        </Field>
                        <ErrorMessage name={field_name} component="div" className="error-message" />
                      </div>
                    );
                  }

                  // For 3-column CSV: Show medicine selection first
                  return (
                    <div style={{ marginTop: '15px' }}>
                      <label className='label-txt'>
                        Select {field?.csvHeaders?.[1] || 'Medicines'} (Multiple)
                      </label>

                      <Field name={`${field_name}_medicine`}>
                        {({ field: medicineField, form }) => {
                          const selectedMedicines = Array.isArray(medicineField.value) ? medicineField.value : [];

                          const filteredMedicines = Object.keys(medicines).filter(medicine =>
                            medicine.toLowerCase().includes(medSearchTerm.toLowerCase())
                          );

                          const handleMedicineSelect = (medicine) => {
                            const newValues = selectedMedicines.includes(medicine)
                              ? selectedMedicines.filter(m => m !== medicine)
                              : [...selectedMedicines, medicine];

                            form.setFieldValue(`${field_name}_medicine`, newValues);
                            form.setFieldValue(field_name, []);
                            setIsMedOpen(false);
                          };

                          const removeMedicine = (medicine) => {
                            const newValues = selectedMedicines.filter(m => m !== medicine);
                            form.setFieldValue(`${field_name}_medicine`, newValues);
                            form.setFieldValue(field_name, []);
                            form.setFieldValue(field_name, '');
                          };

                          return (
                            <div style={{ position: 'relative' }}>
                              {/* Selected Medicine Chips */}
                              {selectedMedicines.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                  marginBottom: '10px',
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: '#f9f9f9',
                                  minHeight: '40px',
                                  alignItems: 'center'
                                }}>
                                  {selectedMedicines.map((medicine, medIndex) => (
                                    <div
                                      key={`med_chip_${medIndex}`}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        padding: '4px 8px',
                                        borderRadius: '16px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        border: '1px solid #bbdefb'
                                      }}
                                    >
                                      {medicine}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeMedicine(medicine);
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          marginLeft: '6px',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          color: '#1976d2',
                                          padding: '0',
                                          lineHeight: '1'
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Medicine Search Input */}
                              <div
                                style={{ position: 'relative' }}
                                onClick={() => setIsMedOpen(!isMedOpen)}
                              >
                                <input
                                  type="text"
                                  placeholder="Search medicines..."
                                  value={medSearchTerm}
                                  onChange={(e) => {
                                    setMedSearchTerm(e.target.value);
                                    setIsMedOpen(true);
                                  }}
                                  onFocus={() => setIsMedOpen(true)}
                                  className="form-control"
                                  style={{ paddingRight: '30px', cursor: 'pointer' }}
                                  readOnly
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#666'
                                  }}
                                >
                                  {isMedOpen ? '▲' : '▼'}
                                </div>
                              </div>

                              {isMedOpen && (
                                <>
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    right: '0',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderTop: 'none',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    borderRadius: '0 0 4px 4px'
                                  }}>
                                    {filteredMedicines.map((medicine, medIndex) => {
                                      const isSelected = selectedMedicines.includes(medicine);

                                      return (
                                        <div
                                          key={`med_${medIndex}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMedicineSelect(medicine);
                                          }}
                                          style={{
                                            padding: '10px 15px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                            borderBottom: medIndex < filteredMedicines.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '14px',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            style={{
                                              marginRight: '10px',
                                              cursor: 'pointer'
                                            }}
                                          />
                                          {medicine}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div
                                    style={{
                                      position: 'fixed',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      zIndex: 999
                                    }}
                                    onClick={() => setIsMedOpen(false)}
                                  />
                                </>
                              )}
                            </div>
                          );
                        }}
                      </Field>
                      <ErrorMessage name={`${field_name}_medicine`} component="div" className="error-message" />
                    </div>
                  );
                }}
              </Field>

              {/* Third Field - MG Multi-Selection (Only for 3-column CSV) */}
              <Field name={`${field_name}_medicine`}>
                {({ field: medicineField }) => {
                  const selectedMedicines = Array.isArray(medicineField.value) ? medicineField.value : [];
                  const selectedCategory = values[`${field_name}_category`];
                  const csvMode = field?.csvMode || (field?.csvHeaders?.length === 2 ? '2-column' : '3-column');

                  // Only show this for 3-column mode
                  if (csvMode === '2-column' || !selectedMedicines.length || !selectedCategory) return null;

                  // Combine MG options from all selected medicines
                  const allMgOptions = selectedMedicines.reduce((acc, medicine) => {
                    const mgs = field?.medicineData?.[selectedCategory]?.[medicine] || [];
                    return [...acc, ...mgs];
                  }, []);

                  // Remove duplicates
                  const uniqueMgOptions = [...new Set(allMgOptions)];

                  return (
                    <div style={{ marginTop: '15px' }}>
                      <Field name={field_name}>
                        {({ field: optionsField, form }) => {
                          // Handle both array and formatted string values
                          let selectedValues = [];
                          if (Array.isArray(optionsField.value)) {
                            selectedValues = optionsField.value;
                          } else if (typeof optionsField.value === 'string' && optionsField.value.includes(' - ')) {
                            const parts = optionsField.value.split(' - ');
                            if (parts.length === 3) {
                              selectedValues = parts[2].split(' , ').map(s => s.trim());
                            }
                          }

                          const filteredOptions = uniqueMgOptions.filter(option =>
                            String(option).toLowerCase().includes(searchTerm.toLowerCase())
                          );

                          const updateBackendValue = (newMgValues) => {
                            const category = form.values[`${field_name}_category`];
                            const medicines = form.values[`${field_name}_medicine`] || [];

                            if (category && medicines.length > 0 && newMgValues.length > 0) {
                              const medicineStr = medicines.join(' , ');
                              const mgStr = newMgValues.join(' , ');
                              const formattedValue = `${category} - ${medicineStr} - ${mgStr}`;
                              form.setFieldValue(field_name, formattedValue);
                            } else {
                              form.setFieldValue(field_name, newMgValues);
                            }
                          };

                          const handleOptionSelect = (optionValue) => {
                            const newValues = selectedValues.includes(optionValue)
                              ? selectedValues.filter(val => val !== optionValue)
                              : [...selectedValues, optionValue];

                            updateBackendValue(newValues);
                          };

                          const removeChip = (optionValue) => {
                            const newValues = selectedValues.filter(val => val !== optionValue);
                            updateBackendValue(newValues);
                          };

                          // Image handling functions (same as before)...
                          const handleCameraCapture = (event) => {
                            const file = event.target.files[0];
                            if (file) {
                              handleImageFile(file, 'camera');
                            }
                          };

                          const handleImageUpload = (event) => {
                            const file = event.target.files[0];
                            if (file) {
                              handleImageFile(file, 'upload');
                            }
                          };

                          const handleImageFile = (file, source) => {
                            const previewReader = new FileReader();
                            previewReader.onload = (e) => {
                              const imageData = {
                                file: file,
                                preview: e.target.result,
                                name: file.name,
                                source: source,
                                timestamp: new Date().toISOString(),
                                size: file.size,
                                type: file.type
                              };

                              const imageFieldName = `${field_name}_images`;
                              const existingImages = values[imageFieldName] || [];
                              const newImages = [...existingImages, imageData];
                              form.setFieldValue(imageFieldName, newImages);

                              const binaryFieldName = `${field_name}_binary_files`;
                              const existingBinaryFiles = values[binaryFieldName] || [];
                              const newBinaryFiles = [...existingBinaryFiles, file];
                              form.setFieldValue(binaryFieldName, newBinaryFiles);

                              alert(`${source === 'camera' ? 'Photo captured' : 'Image uploaded'} successfully!`);
                            };
                            previewReader.readAsDataURL(file);
                          };

                          const removeImage = (imageIndex) => {
                            const imageFieldName = `${field_name}_images`;
                            const binaryFieldName = `${field_name}_binary_files`;

                            const existingImages = values[imageFieldName] || [];
                            const existingBinaryFiles = values[binaryFieldName] || [];

                            const newImages = existingImages.filter((_, index) => index !== imageIndex);
                            const newBinaryFiles = existingBinaryFiles.filter((_, index) => index !== imageIndex);

                            form.setFieldValue(imageFieldName, newImages);
                            form.setFieldValue(binaryFieldName, newBinaryFiles);
                          };

                          const uploadedImages = values[`${field_name}_images`] || [];

                          return (
                            <div style={{ position: 'relative' }}>
                              <label className='label-txt'>
                                Select {field?.csvHeaders?.[2] || 'MG Options'} (Multiple)
                              </label>

                              {/* Selected MG Chips */}
                              {selectedValues.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                  marginBottom: '10px',
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: '#f9f9f9',
                                  minHeight: '40px',
                                  alignItems: 'center'
                                }}>
                                  {selectedValues.map((value, chipIndex) => (
                                    <div
                                      key={`chip_${chipIndex}`}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        padding: '4px 8px',
                                        borderRadius: '16px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        border: '1px solid #bbdefb'
                                      }}
                                    >
                                      {value}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeChip(value);
                                        }}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          marginLeft: '6px',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          color: '#1976d2',
                                          padding: '0',
                                          lineHeight: '1'
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* MG Search Input */}
                              <div
                                style={{ position: 'relative' }}
                                onClick={() => setIsOpen(!isOpen)}
                              >
                                <input
                                  type="text"
                                  placeholder="Search MG options..."
                                  value={searchTerm}
                                  onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setIsOpen(true);
                                  }}
                                  onFocus={() => setIsOpen(true)}
                                  className="form-control"
                                  style={{ paddingRight: '30px', cursor: 'pointer' }}
                                  readOnly
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#666'
                                  }}
                                >
                                  {isOpen ? '▲' : '▼'}
                                </div>
                              </div>

                              {/* MG Dropdown */}
                              {isOpen && (
                                <>
                                  <div style={{
                                    position: 'absolute',
                                    top: 'auto',
                                    left: '0',
                                    right: '0',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderTop: 'none',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    borderRadius: '0 0 4px 4px'
                                  }}>
                                    {filteredOptions.map((option, optionIndex) => {
                                      const optionValue = String(option);
                                      const isSelected = selectedValues.includes(optionValue);

                                      return (
                                        <div
                                          key={`option_${optionIndex}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOptionSelect(optionValue);
                                          }}
                                          style={{
                                            padding: '10px 15px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                            borderBottom: optionIndex < filteredOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '14px',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            style={{
                                              marginRight: '10px',
                                              cursor: 'pointer'
                                            }}
                                          />
                                          {option}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div
                                    style={{
                                      position: 'fixed',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      zIndex: 999
                                    }}
                                    onClick={() => setIsOpen(false)}
                                  />
                                </>
                              )}

                              {/* Image Upload Section */}
                              <div style={{
                                marginTop: '15px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '2px dashed #dee2e6'
                              }}>
                                <p style={{
                                  margin: '0 0 10px 0',
                                  fontSize: '14px',
                                  color: '#666',
                                  fontWeight: '500'
                                }}>
                                  Can't find your {selectedCategory.toLowerCase()}? Upload an image or take a photo:
                                </p>

                                <div style={{
                                  display: 'flex',
                                  gap: '10px',
                                  flexWrap: 'wrap',
                                  marginBottom: uploadedImages.length > 0 ? '15px' : '0'
                                }}>
                                  <label style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s ease'
                                  }}>
                                    📷 Take Photo
                                    <input
                                      type="file"
                                      accept="image/*"
                                      capture="environment"
                                      onChange={handleCameraCapture}
                                      style={{ display: 'none' }}
                                    />
                                  </label>

                                  <label style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s ease'
                                  }}>
                                    📁 Upload Image
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handleImageUpload}
                                      style={{ display: 'none' }}
                                    />
                                  </label>
                                </div>

                                {uploadedImages.length > 0 && (
                                  <div>
                                    <h4 style={{
                                      fontSize: '16px',
                                      margin: '0 0 10px 0',
                                      color: '#333'
                                    }}>
                                      Uploaded Images ({uploadedImages.length})
                                    </h4>

                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                      gap: '10px'
                                    }}>
                                      {uploadedImages.map((image, imageIndex) => (
                                        <div
                                          key={`uploaded_image_${imageIndex}`}
                                          style={{
                                            position: 'relative',
                                            border: '2px solid #ddd',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            backgroundColor: 'white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                          }}
                                        >
                                          <img
                                            src={image.preview}
                                            alt={image.name}
                                            style={{
                                              width: '100%',
                                              height: '120px',
                                              objectFit: 'cover',
                                              display: 'block'
                                            }}
                                          />

                                          <div style={{
                                            position: 'absolute',
                                            top: '5px',
                                            right: '5px',
                                            backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold'
                                          }}
                                            onClick={() => removeImage(imageIndex)}
                                            title="Remove image"
                                          >
                                            ×
                                          </div>

                                          <div style={{
                                            position: 'absolute',
                                            top: '5px',
                                            left: '5px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                            color: 'white',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                          }}>
                                            {image.source === 'camera' ? '📷' : '📁'}
                                          </div>

                                          <div style={{
                                            padding: '5px',
                                            fontSize: '10px',
                                            color: '#666',
                                            textAlign: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderTop: '1px solid #eee'
                                          }}>
                                            <div style={{
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              marginBottom: '2px'
                                            }}>
                                              {image.name}
                                            </div>
                                            <div style={{ fontSize: '9px', color: '#888' }}>
                                              {(image.size / 1024).toFixed(1)} KB
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }}
                      </Field>
                      <ErrorMessage name={field_name} component="div" className="error-message" />
                    </div>
                  );
                }}
              </Field>
            </div>
          );

        case 'radio':
          return (
            <div key={fieldKey} className="form-group">
              {!field.hideLabel && (
                <label className="form-label label-txt">
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}

              <div className={`radio-group grid-5 ${isAuthorizedContacts ? 'authorized-contacts-grid' : ''}`}>
                <Field name={field_name}>
                  {({ field: formikField, form }) => {
                    return field_options?.map((option, optionIndex) => {
                      if (!option) return null;

                      const optionValue = typeof option === 'object' ? option.value : String(option);
                      const optionLabel = typeof option === 'object' ? option.label : String(option);

                      // Enhanced logic for checking if radio is selected
                      const isSelected = field_name === 'gender'
                        ? (optionValue === 'Male' && (formikField.value === 'man' || formikField.value?.startsWith('Male'))) ||
                        (optionValue === 'Female' && (formikField.value === 'woman' || formikField.value?.startsWith('Female'))) ||
                        (optionValue === 'Decline to specify' && (formikField.value === 'woman' || formikField.value?.startsWith('Decline to specify')))
                        : formikField.value === optionValue || formikField.value?.startsWith(`${optionValue} - `);

                      return (
                        <label
                          key={`${fieldKey}_radio_${optionIndex}`}
                          className={`radio-label gender-label ${isSelected ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name={formikField.name}
                            value={optionValue}
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={(e) => {
                              // If already selected, uncheck it
                              if (isSelected) {
                                form.setFieldValue(field_name, '');
                                if (field_name === 'gender') {
                                  setCanvasGender?.('');
                                }
                              } else {
                                // Select the option
                                form.setFieldValue(field_name, optionValue);

                                if (field_name === 'gender') {
                                  // Map for canvas
                                  let genderValue = optionValue === 'Male'
                                    ? 'man'
                                    : optionValue === 'Female'
                                      ? 'woman'
                                      : optionValue === 'Decline to specify'
                                        ? 'woman'
                                        : '';

                                  setCanvasGender?.(genderValue);
                                }
                              }

                              // Reset conditional fields when radio selection changes
                              field.optionsWithConditional?.forEach((cfg, cfgIndex) => {
                                if (cfg.conditionalInputs && Array.isArray(cfg.conditionalInputs)) {
                                  cfg.conditionalInputs.forEach((_, conditionalIndex) => {
                                    const conditionalFieldName = `${field_name}_conditional_${cfgIndex}_${conditionalIndex}`;
                                    form.setFieldValue(conditionalFieldName, '');
                                  });
                                }
                              });
                            }}
                          />
                          <span className="radio-text">{optionLabel}</span>
                        </label>
                      );
                    });
                  }}
                </Field>
              </div>

              {/* Conditional Fields Rendering */}
              <Field name={field_name}>
                {({ field: formikField, form }) => {
                  return field_options?.map((option, optionIndex) => {
                    const optionValue = typeof option === 'object' ? option.value : String(option);
                    const optionLabel = typeof option === 'object' ? option.label : String(option);

                    // Same enhanced logic for conditional rendering
                    const isSelected = formikField.value === optionValue ||
                      formikField.value?.startsWith(`${optionValue} - `) ||
                      (field_name === 'gender' && (
                        (optionValue === 'Male' && (formikField.value === 'man' || formikField.value?.startsWith('Male'))) ||
                        (optionValue === 'Female' && (formikField.value === 'woman' || formikField.value?.startsWith('Female'))) ||
                        (optionValue === 'Decline to specify' && (formikField.value === 'woman' || formikField.value?.startsWith('Decline to specify')))
                      ));

                    // Enhanced matching: try exact match first, then case-insensitive, then trimmed
                    let config = field.optionsWithConditional?.find((cfg) =>
                      cfg.label === optionLabel || cfg.label === optionValue
                    );

                    // Fallback: try case-insensitive matching if exact match fails
                    if (!config && field.optionsWithConditional) {
                      config = field.optionsWithConditional.find((cfg) =>
                        cfg.label?.toLowerCase()?.trim() === optionLabel?.toLowerCase()?.trim() ||
                        cfg.label?.toLowerCase()?.trim() === optionValue?.toLowerCase()?.trim()
                      );
                    }

                    // Debug logging for troubleshooting
                    if (isSelected) {
                      logger.log('Conditional rendering check:', {
                        field_name,
                        optionLabel,
                        optionValue,
                        optionsWithConditional: field.optionsWithConditional,
                        foundConfig: config,
                        conditionalInputs: config?.conditionalInputs
                      });
                    }

                    if (isSelected && config?.conditionalInputs && Array.isArray(config.conditionalInputs) && config.conditionalInputs.length > 0) {
                      // Separate regular conditional inputs from separateSection inputs
                      const regularInputs = config.conditionalInputs.filter(input => input.type !== 'separateSection');
                      const separateSectionInputs = config.conditionalInputs.filter(input => input.type === 'separateSection');

                      const hasRadioOrCheckbox = regularInputs.some(input =>
                        input.type === 'radio' || input.type === 'checkbox'
                      );

                      // Find the actual index of config in optionsWithConditional to match FormPage.jsx initialization
                      const configIndex = field.optionsWithConditional?.findIndex(cfg => cfg === config) ?? optionIndex;

                      return (
                        <div key={`conditional_${optionIndex}`}>
                          {/* Regular conditional inputs in grid */}
                          {regularInputs.length > 0 && (
                            <div className={hasRadioOrCheckbox ? `grid-5 ${isAuthorizedContacts ? 'authorized-contacts-grid' : ''}` : "conditional-inputs-container flex-wrap"} style={{ marginTop: '15px' }}>
                              {regularInputs.map((conditionalInput, idx) => {
                                // Get original index for field naming
                                const conditionalIndex = config.conditionalInputs.findIndex(ci => ci === conditionalInput);
                                const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;

                                return (
                                  <div
                                    key={`conditional_input_${conditionalIndex}`}
                                    style={{
                                      width: window.innerWidth <= 767 ? '100%' : `${conditionalInput?.fieldWidth || 100}%`,
                                    }}>
                                  <Field name={conditionalFieldName}>
                                    {({ field: conditionalField, form: conditionalForm }) => {
                                      // Date input handling with MM/DD/YYYY format
                                      if (conditionalInput.type === 'date') {
                                        return (
                                          <div className="form-group">
                                            {conditionalInput.label && (
                                              <label className="form-label label-txt">{conditionalInput.label}</label>
                                            )}
                                            <DatePicker
                                              selected={conditionalField.value ? new Date(conditionalField.value) : null}
                                              onChange={(date) => {
                                                const formattedDate = date ? date.toLocaleDateString('en-US') : '';
                                                form.setFieldValue(conditionalFieldName, formattedDate);

                                                if (formattedDate.trim()) {
                                                  form.setFieldValue(field_name, `${optionValue} - ${formattedDate}`);
                                                } else {
                                                  form.setFieldValue(field_name, optionValue);
                                                }
                                              }}
                                              dateFormat="MM/dd/yyyy"
                                              placeholderText={conditionalInput.placeholder || 'Select date (MM/DD/YYYY)'}
                                              className="form-control"
                                              showPopperArrow={false}
                                              autoComplete="off"
                                              maxDate={new Date()}
                                            />
                                          </div>
                                        );
                                      }

                                      // Radio button handling with proper selected class
                                      else if (conditionalInput.type === 'radio') {
                                        // Check if this nested radio has its own options
                                        const nestedOptions = conditionalInput.nestedConditionals || [];

                                        if (nestedOptions.length > 0) {
                                          // First render the main conditional radio input
                                          const isMainSelected = conditionalField.value === conditionalInput.label;

                                          return (
                                            <div className="conditional-label label-txt">
                                              {/* Main conditional radio input */}
                                              <label className={`radio-label gender-label ${isMainSelected ? 'selected' : ''}`}>
                                                <input
                                                  type="radio"
                                                  name={`${field_name}_conditional_${configIndex}`}
                                                  value={conditionalInput.label}
                                                  checked={isMainSelected}
                                                  onChange={(e) => {
                                                    // Clear other conditional fields first
                                                    config.conditionalInputs.forEach((_, idx) => {
                                                      if (idx !== conditionalIndex) {
                                                        const otherFieldName = `${field_name}_conditional_${configIndex}_${idx}`;
                                                        form.setFieldValue(otherFieldName, '');
                                                        form.setFieldValue(`${otherFieldName}_nested`, '');
                                                      }
                                                    });

                                                    form.setFieldValue(conditionalFieldName, e.target.value);
                                                    form.setFieldValue(`${conditionalFieldName}_nested`, '');
                                                    // Update main field value with the selected conditional option
                                                    form.setFieldValue(field_name, `${optionValue} - ${e.target.value}`);
                                                  }}
                                                />
                                                <span>{conditionalInput.label}</span>
                                              </label>
                                            </div>
                                          );
                                        } else {
                                          // Single radio option
                                          const isConditionalSelected = conditionalField.value === conditionalInput.label;

                                          return (
                                            <div className="conditional-label label-txt">
                                              <label className={`radio-label gender-label ${isConditionalSelected ? 'selected' : ''}`}>
                                                <input
                                                  type="radio"
                                                  name={`${field_name}_conditional_${configIndex}`}
                                                  value={conditionalInput.label}
                                                  checked={isConditionalSelected}
                                                  onChange={(e) => {
                                                    config.conditionalInputs.forEach((_, idx) => {
                                                      if (idx !== conditionalIndex) {
                                                        const otherFieldName = `${field_name}_conditional_${configIndex}_${idx}`;
                                                        form.setFieldValue(otherFieldName, '');
                                                      }
                                                    });
                                                    form.setFieldValue(conditionalFieldName, e.target.value);
                                                    // Update main field value with the selected conditional option
                                                    form.setFieldValue(field_name, `${optionValue} - ${e.target.value}`);
                                                  }}
                                                />
                                                <span>{conditionalInput.label || "Check this option"}</span>
                                              </label>
                                            </div>
                                          );
                                        }
                                      }

                                      else if (conditionalInput.type === 'checkbox') {
                                        const isChecked = conditionalField.value === true || conditionalField.value === 'true';
                                        const nestedOptions = conditionalInput.nestedConditionals || [];

                                        return (
                                          <div className="conditional-label label-txt">
                                            <label className={`checkbox-label gender-label ${isChecked ? 'selected' : ''}`}>
                                              <input
                                                type="checkbox"
                                                name={conditionalFieldName}
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  const isChecked = e.target.checked;
                                                  form.setFieldValue(conditionalFieldName, isChecked);

                                                  if (isChecked) {
                                                    form.setFieldValue(field_name, `${optionValue} - ${conditionalInput.label || 'Selected'}`);
                                                  } else {
                                                    form.setFieldValue(field_name, optionValue);

                                                    // Clear nested conditionals when checkbox is unchecked
                                                    nestedOptions.forEach((nestedOption, nestedIndex) => {
                                                      form.setFieldValue(`${conditionalFieldName}_nested`, '');
                                                    });
                                                  }
                                                }}
                                              />
                                              <span>{conditionalInput.label || 'Check this option'}</span>
                                            </label>
                                          </div>
                                        );
                                      }

                                      // Textarea handling
                                      else if (conditionalInput.type === 'textarea') {
                                        return (
                                          <div className="form-group">
                                            {conditionalInput.label && (
                                              <label className="form-label label-txt">{conditionalInput.label}</label>
                                            )}
                                            <textarea
                                              {...conditionalField}
                                              placeholder={conditionalInput.placeholder || 'Enter info'}
                                              className="form-control"
                                              rows="3"
                                              onChange={(e) => {
                                                const inputValue = e.target.value;
                                                form.setFieldValue(conditionalFieldName, inputValue);

                                                if (inputValue.trim()) {
                                                  form.setFieldValue(field_name, `${optionValue} - ${inputValue}`);
                                                } else {
                                                  form.setFieldValue(field_name, optionValue);
                                                }
                                              }}
                                            />
                                          </div>
                                        );
                                      }

                                      // Number input with increment/decrement buttons
                                      else if (conditionalInput.type === 'number') {
                                        const currentVal = conditionalField.value !== undefined && conditionalField.value !== '' ? Number(conditionalField.value) : 0;
                                        return (
                                          <div className="form-group">
                                            {conditionalInput.label && (
                                              <label className="form-label label-txt">{conditionalInput.label}</label>
                                            )}
                                            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                              <input
                                                {...conditionalField}
                                                type="number"
                                                placeholder={conditionalInput.placeholder || 'Enter number'}
                                                className="form-control"
                                                style={{ paddingRight: '35px' }}
                                                onChange={(e) => {
                                                  const inputValue = e.target.value;
                                                  form.setFieldValue(conditionalFieldName, inputValue);

                                                  if (inputValue && inputValue.toString().trim()) {
                                                    form.setFieldValue(field_name, `${optionValue} - ${inputValue}`);
                                                  } else {
                                                    form.setFieldValue(field_name, optionValue);
                                                  }
                                                }}
                                              />
                                              <div style={{
                                                position: 'absolute',
                                                right: '1px',
                                                top: '1px',
                                                bottom: '1px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderLeft: '1px solid #ced4da',
                                                background: '#f8f9fa',
                                                borderRadius: '0 4px 4px 0',
                                                margin: '2px'
                                              }}>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newVal = currentVal + 1;
                                                    form.setFieldValue(conditionalFieldName, newVal);
                                                    form.setFieldValue(field_name, `${optionValue} - ${newVal}`);
                                                  }}
                                                  style={{
                                                    flex: 1,
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    padding: '0 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderBottom: '1px solid #ced4da',
                                                    fontSize: '10px',
                                                    color: '#495057'
                                                  }}
                                                >
                                                  ▲
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newVal = currentVal - 1;
                                                    form.setFieldValue(conditionalFieldName, newVal);
                                                    form.setFieldValue(field_name, `${optionValue} - ${newVal}`);
                                                  }}
                                                  style={{
                                                    flex: 1,
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    padding: '0 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    color: '#495057'
                                                  }}
                                                >
                                                  ▼
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Separate Section Fields handling
                                      else if (conditionalInput.type === 'separateSection') {
                                        // Debug log
                                        logger.log('SeparateSection rendering:', {
                                          label: conditionalInput.label,
                                          sectionFields: conditionalInput.sectionFields,
                                          conditionalFieldName
                                        });

                                        // Check if user created multiple radio fields with labels (old pattern)
                                        // instead of one radio field with options (correct pattern)
                                        const radioFieldsWithLabels = conditionalInput.sectionFields?.filter(
                                          sf => sf.type === 'radio' && sf.label && (!sf.options || sf.options.length === 0)
                                        ) || [];

                                        // If we have multiple radio fields with labels but no options,
                                        // combine them into a single radio group
                                        const shouldCombineRadioLabels = radioFieldsWithLabels.length > 1;

                                        if (shouldCombineRadioLabels) {
                                          // Combine all radio labels into one radio group
                                          const combinedOptions = radioFieldsWithLabels.map(sf => sf.label);
                                          const combinedFieldName = `${conditionalFieldName}_section_combined`;

                                          return (
                                            <div className="separate-section-container" style={{
                                              width: '100%',
                                              marginTop: '10px'
                                            }}>
                                              {conditionalInput.label && (
                                                <label className="form-label label-txt" style={{
                                                  display: 'block',
                                                  marginBottom: '10px',
                                                  fontWeight: '500'
                                                }}>
                                                  {conditionalInput.label}
                                                </label>
                                              )}
                                              <Field name={combinedFieldName}>
                                                {({ field: sfField, form: sfForm }) => (
                                                  <div className="grid-5">
                                                    {combinedOptions.map((opt, optIdx) => {
                                                      const isSelected = sfField.value === opt;
                                                      return (
                                                        <div key={optIdx} className="conditional-label label-txt">
                                                          <label className={`radio-label gender-label ${isSelected ? 'selected' : ''}`}>
                                                            <input
                                                              type="radio"
                                                              name={combinedFieldName}
                                                              value={opt}
                                                              checked={isSelected}
                                                              onChange={() => sfForm.setFieldValue(combinedFieldName, opt)}
                                                            />
                                                            <span>{opt}</span>
                                                          </label>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </Field>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div className="separate-section-container" style={{
                                            width: '100%',
                                            marginTop: '10px'
                                          }}>
                                            {conditionalInput.label && (
                                              <label className="form-label label-txt" style={{
                                                display: 'block',
                                                marginBottom: '10px',
                                                fontWeight: '500'
                                              }}>
                                                {conditionalInput.label}
                                              </label>
                                            )}
                                            {/* Render section fields - Radio fields in grid-5 pattern */}
                                            {conditionalInput.sectionFields?.map((sectionField, sfIndex) => {
                                              const sectionFieldName = `${conditionalFieldName}_section_${sfIndex}`;

                                              // Radio - render as grid-5 like other conditional radios
                                              if (sectionField.type === 'radio') {
                                                // If this radio field has options, render them
                                                if (sectionField.options && sectionField.options.length > 0) {
                                                  return (
                                                    <Field key={sfIndex} name={sectionFieldName}>
                                                      {({ field: sfField, form: sfForm }) => (
                                                        <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                          {sectionField.options.map((opt, optIdx) => {
                                                            const isSelected = sfField.value === opt;
                                                            return (
                                                              <div key={optIdx} className="conditional-label label-txt">
                                                                <label className={`radio-label gender-label ${isSelected ? 'selected' : ''}`}>
                                                                  <input
                                                                    type="radio"
                                                                    name={sectionFieldName}
                                                                    value={opt}
                                                                    checked={isSelected}
                                                                    onChange={() => sfForm.setFieldValue(sectionFieldName, opt)}
                                                                  />
                                                                  <span>{opt}</span>
                                                                </label>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      )}
                                                    </Field>
                                                  );
                                                }
                                                // If radio field has only label (single field case), show as single option
                                                else if (sectionField.label) {
                                                  return (
                                                    <Field key={sfIndex} name={sectionFieldName}>
                                                      {({ field: sfField, form: sfForm }) => (
                                                        <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                          <div className="conditional-label label-txt">
                                                            <label className={`radio-label gender-label ${sfField.value === sectionField.label ? 'selected' : ''}`}>
                                                              <input
                                                                type="radio"
                                                                name={sectionFieldName}
                                                                value={sectionField.label}
                                                                checked={sfField.value === sectionField.label}
                                                                onChange={() => sfForm.setFieldValue(sectionFieldName, sectionField.label)}
                                                              />
                                                              <span>{sectionField.label}</span>
                                                            </label>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </Field>
                                                  );
                                                }
                                                return null;
                                              }

                                              // Checkbox - render as grid-5 like other conditional checkboxes
                                              if (sectionField.type === 'checkbox') {
                                                return (
                                                  <Field key={sfIndex} name={sectionFieldName}>
                                                    {({ field: sfField, form: sfForm }) => {
                                                      const currentValues = Array.isArray(sfField.value) ? sfField.value : [];
                                                      return (
                                                        <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                          {sectionField.options?.map((opt, optIdx) => {
                                                            const isChecked = currentValues.includes(opt);
                                                            return (
                                                              <div key={optIdx} className="conditional-label label-txt">
                                                                <label className={`checkbox-label gender-label ${isChecked ? 'selected' : ''}`}>
                                                                  <input
                                                                    type="checkbox"
                                                                    name={sectionFieldName}
                                                                    value={opt}
                                                                    checked={isChecked}
                                                                    onChange={(e) => {
                                                                      const newValues = e.target.checked
                                                                        ? [...currentValues, opt]
                                                                        : currentValues.filter(v => v !== opt);
                                                                      sfForm.setFieldValue(sectionFieldName, newValues);
                                                                    }}
                                                                  />
                                                                  <span>{opt}</span>
                                                                </label>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      );
                                                    }}
                                                  </Field>
                                                );
                                              }

                                              // Text input
                                              if (sectionField.type === 'text' || sectionField.type === 'email' || sectionField.type === 'tel') {
                                                return (
                                                  <Field key={sfIndex} name={sectionFieldName}>
                                                    {({ field: sfField }) => (
                                                      <div className="form-group" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                        {sectionField.label && (
                                                          <label className="form-label label-txt">
                                                            {sectionField.label}
                                                            {sectionField.required && <span className="required">*</span>}
                                                          </label>
                                                        )}
                                                        <input
                                                          {...sfField}
                                                          type={sectionField.type}
                                                          placeholder={sectionField.placeholder || ''}
                                                          className="form-control"
                                                        />
                                                      </div>
                                                    )}
                                                  </Field>
                                                );
                                              }

                                              // Textarea
                                              if (sectionField.type === 'textarea') {
                                                return (
                                                  <Field key={sfIndex} name={sectionFieldName}>
                                                    {({ field: sfField }) => (
                                                      <div className="form-group" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                        {sectionField.label && (
                                                          <label className="form-label label-txt">
                                                            {sectionField.label}
                                                            {sectionField.required && <span className="required">*</span>}
                                                          </label>
                                                        )}
                                                        <textarea
                                                          {...sfField}
                                                          placeholder={sectionField.placeholder || ''}
                                                          className="form-control"
                                                          rows="3"
                                                        />
                                                      </div>
                                                    )}
                                                  </Field>
                                                );
                                              }

                                              // Number
                                              if (sectionField.type === 'number') {
                                                return (
                                                  <Field key={sfIndex} name={sectionFieldName}>
                                                    {({ field: sfField }) => (
                                                      <div className="form-group" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                        {sectionField.label && (
                                                          <label className="form-label label-txt">
                                                            {sectionField.label}
                                                            {sectionField.required && <span className="required">*</span>}
                                                          </label>
                                                        )}
                                                        <input
                                                          {...sfField}
                                                          type="number"
                                                          placeholder={sectionField.placeholder || ''}
                                                          className="form-control"
                                                        />
                                                      </div>
                                                    )}
                                                  </Field>
                                                );
                                              }

                                              // Date
                                              if (sectionField.type === 'date') {
                                                return (
                                                  <Field key={sfIndex} name={sectionFieldName}>
                                                    {({ field: sfField }) => (
                                                      <div className="form-group" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                                        {sectionField.label && (
                                                          <label className="form-label label-txt">
                                                            {sectionField.label}
                                                            {sectionField.required && <span className="required">*</span>}
                                                          </label>
                                                        )}
                                                        <input
                                                          {...sfField}
                                                          type="date"
                                                          className="form-control"
                                                        />
                                                      </div>
                                                    )}
                                                  </Field>
                                                );
                                              }

                                              return null;
                                            })}
                                          </div>
                                        );
                                      }

                                      // Default text input and other types
                                      else {
                                        return (
                                          <div className="form-group">
                                            {conditionalInput.label && (
                                              <label className="form-label label-txt">{conditionalInput.label}</label>
                                            )}
                                            <input
                                              {...conditionalField}
                                              type={conditionalInput.type || 'text'}
                                              placeholder={conditionalInput.placeholder || 'Enter info'}
                                              className="form-control"
                                              onChange={(e) => {
                                                const inputValue = e.target.value;
                                                form.setFieldValue(conditionalFieldName, inputValue);

                                                if (inputValue.trim()) {
                                                  form.setFieldValue(field_name, `${optionValue} - ${inputValue}`);
                                                } else {
                                                  form.setFieldValue(field_name, optionValue);
                                                }
                                              }}
                                            />
                                          </div>
                                        );
                                      }
                                    }}
                                  </Field>
                                  <ErrorMessage name={conditionalFieldName} component="div" className="error-message" />
                                </div>
                              );
                            })}
                          </div>
                          )}

                          {/* Separate Section Inputs - rendered OUTSIDE the grid as full width */}
                          {separateSectionInputs.map((conditionalInput, idx) => {
                            const conditionalIndex = config.conditionalInputs.findIndex(ci => ci === conditionalInput);
                            const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;

                            // Check if user created multiple radio fields with labels (old pattern)
                            const radioFieldsWithLabels = conditionalInput.sectionFields?.filter(
                              sf => sf.type === 'radio' && sf.label && (!sf.options || sf.options.length === 0)
                            ) || [];

                            const shouldCombineRadioLabels = radioFieldsWithLabels.length > 1;

                            if (shouldCombineRadioLabels) {
                              const combinedOptions = radioFieldsWithLabels.map(sf => sf.label);
                              const combinedFieldName = `${conditionalFieldName}_section_combined`;
                              const sectionLabel = conditionalInput.label || 'Section';

                              return (
                                <div key={`separate_section_${conditionalIndex}`} style={{ width: '100%', marginTop: '15px' }}>
                                  {conditionalInput.label && (
                                    <label className="form-label label-txt" style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                                      {conditionalInput.label}
                                    </label>
                                  )}
                                  <Field name={combinedFieldName}>
                                    {({ field: sfField, form: sfForm }) => (
                                      <div className="grid-5">
                                        {combinedOptions.map((opt, optIdx) => {
                                          const isSelected = sfField.value === opt;
                                          return (
                                            <div key={optIdx} className="conditional-label label-txt">
                                              <label className={`radio-label gender-label ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                  type="radio"
                                                  name={combinedFieldName}
                                                  value={opt}
                                                  checked={isSelected}
                                                  onChange={() => {
                                                    sfForm.setFieldValue(combinedFieldName, opt);
                                                    // Update main field value to include separateSection selection
                                                    const currentMainValue = sfForm.values[field_name] || optionValue;
                                                    // Remove any previous section value and append new one
                                                    const baseValue = currentMainValue.split(' - ' + sectionLabel)[0];
                                                    sfForm.setFieldValue(field_name, `${baseValue} - ${sectionLabel} - ${opt}`);
                                                  }}
                                                />
                                                <span>{opt}</span>
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </Field>
                                </div>
                              );
                            }

                            const sectionLabelForNormal = conditionalInput.label || 'Section';

                            return (
                              <div key={`separate_section_${conditionalIndex}`} style={{ width: '100%', marginTop: '15px' }}>
                                {conditionalInput.label && (
                                  <label className="form-label label-txt" style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                                    {conditionalInput.label}
                                  </label>
                                )}
                                {conditionalInput.sectionFields?.map((sectionField, sfIndex) => {
                                  const sectionFieldName = `${conditionalFieldName}_section_${sfIndex}`;

                                  if (sectionField.type === 'radio') {
                                    if (sectionField.options && sectionField.options.length > 0) {
                                      return (
                                        <Field key={sfIndex} name={sectionFieldName}>
                                          {({ field: sfField, form: sfForm }) => (
                                            <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                              {sectionField.options.map((opt, optIdx) => {
                                                const isSelected = sfField.value === opt;
                                                return (
                                                  <div key={optIdx} className="conditional-label label-txt">
                                                    <label className={`radio-label gender-label ${isSelected ? 'selected' : ''}`}>
                                                      <input
                                                        type="radio"
                                                        name={sectionFieldName}
                                                        value={opt}
                                                        checked={isSelected}
                                                        onChange={() => {
                                                          sfForm.setFieldValue(sectionFieldName, opt);
                                                          // Update main field value to include separateSection selection
                                                          const currentMainValue = sfForm.values[field_name] || optionValue;
                                                          const baseValue = currentMainValue.split(' - ' + sectionLabelForNormal)[0];
                                                          sfForm.setFieldValue(field_name, `${baseValue} - ${sectionLabelForNormal} - ${opt}`);
                                                        }}
                                                      />
                                                      <span>{opt}</span>
                                                    </label>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </Field>
                                      );
                                    } else if (sectionField.label) {
                                      return (
                                        <Field key={sfIndex} name={sectionFieldName}>
                                          {({ field: sfField, form: sfForm }) => (
                                            <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                              <div className="conditional-label label-txt">
                                                <label className={`radio-label gender-label ${sfField.value === sectionField.label ? 'selected' : ''}`}>
                                                  <input
                                                    type="radio"
                                                    name={sectionFieldName}
                                                    value={sectionField.label}
                                                    checked={sfField.value === sectionField.label}
                                                    onChange={() => {
                                                      sfForm.setFieldValue(sectionFieldName, sectionField.label);
                                                      // Update main field value
                                                      const currentMainValue = sfForm.values[field_name] || optionValue;
                                                      const baseValue = currentMainValue.split(' - ' + sectionLabelForNormal)[0];
                                                      sfForm.setFieldValue(field_name, `${baseValue} - ${sectionLabelForNormal} - ${sectionField.label}`);
                                                    }}
                                                  />
                                                  <span>{sectionField.label}</span>
                                                </label>
                                              </div>
                                            </div>
                                          )}
                                        </Field>
                                      );
                                    }
                                  }

                                  if (sectionField.type === 'checkbox') {
                                    return (
                                      <Field key={sfIndex} name={sectionFieldName}>
                                        {({ field: sfField, form: sfForm }) => {
                                          const currentValues = Array.isArray(sfField.value) ? sfField.value : [];
                                          const options = sectionField.options && sectionField.options.length > 0 ? sectionField.options : [sectionField.label].filter(Boolean);
                                          return (
                                            <div className="grid-5" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                              {options.map((opt, optIdx) => {
                                                const isChecked = currentValues.includes(opt);
                                                return (
                                                  <div key={optIdx} className="conditional-label label-txt">
                                                    <label className={`checkbox-label gender-label ${isChecked ? 'selected' : ''}`}>
                                                      <input
                                                        type="checkbox"
                                                        name={sectionFieldName}
                                                        value={opt}
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                          const newValues = e.target.checked
                                                            ? [...currentValues, opt]
                                                            : currentValues.filter(v => v !== opt);
                                                          sfForm.setFieldValue(sectionFieldName, newValues);
                                                          // Update main field value with checkbox selections
                                                          const currentMainValue = sfForm.values[field_name] || optionValue;
                                                          const baseValue = currentMainValue.split(' - ' + sectionLabelForNormal)[0];
                                                          if (newValues.length > 0) {
                                                            sfForm.setFieldValue(field_name, `${baseValue} - ${sectionLabelForNormal} - ${newValues.join(', ')}`);
                                                          } else {
                                                            sfForm.setFieldValue(field_name, baseValue);
                                                          }
                                                        }}
                                                      />
                                                      <span>{opt}</span>
                                                    </label>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        }}
                                      </Field>
                                    );
                                  }

                                  if (sectionField.type === 'text' || sectionField.type === 'email' || sectionField.type === 'tel') {
                                    return (
                                      <Field key={sfIndex} name={sectionFieldName}>
                                        {({ field: sfField }) => (
                                          <div className="form-group" style={{ marginTop: sfIndex > 0 ? '15px' : '0' }}>
                                            {sectionField.label && (
                                              <label className="form-label label-txt">
                                                {sectionField.label}
                                                {sectionField.required && <span className="required">*</span>}
                                              </label>
                                            )}
                                            <input {...sfField} type={sectionField.type} placeholder={sectionField.placeholder || ''} className="form-control" />
                                          </div>
                                        )}
                                      </Field>
                                    );
                                  }

                                  return null;
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    return null;
                  });
                }}
              </Field>
              <Field name={field_name}>
                {({ field: formikField, form }) => {
                  return field_options?.map((option, optionIndex) => {
                    const optionValue = typeof option === 'object' ? option.value : String(option);
                    const optionLabel = typeof option === 'object' ? option.label : String(option);

                    const isSelected = formikField.value === optionValue ||
                      formikField.value?.startsWith(`${optionValue} - `) ||
                      (field_name === 'gender' && (
                        (optionValue === 'Male' && (formikField.value === 'man' || formikField.value?.startsWith('Male'))) ||
                        (optionValue === 'Female' && (formikField.value === 'woman' || formikField.value?.startsWith('Female'))) ||
                        (optionValue === 'Decline to specify' && (formikField.value === 'woman' || formikField.value?.startsWith('Decline to specify')))
                      ));

                    // Enhanced matching: try exact match first, then case-insensitive
                    let config = field.optionsWithConditional?.find((cfg) =>
                      cfg.label === optionLabel || cfg.label === optionValue
                    );

                    // Fallback: try case-insensitive matching if exact match fails
                    if (!config && field.optionsWithConditional) {
                      config = field.optionsWithConditional.find((cfg) =>
                        cfg.label?.toLowerCase()?.trim() === optionLabel?.toLowerCase()?.trim() ||
                        cfg.label?.toLowerCase()?.trim() === optionValue?.toLowerCase()?.trim()
                      );
                    }

                    if (isSelected && config?.conditionalInputs && Array.isArray(config.conditionalInputs)) {
                      // Find actual config index to match FormPage.jsx initialization
                      const configIndex = field.optionsWithConditional?.findIndex(cfg => cfg === config) ?? optionIndex;

                      return config.conditionalInputs.map((conditionalInput, conditionalIndex) => {
                        const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                        const nestedOptions = conditionalInput.nestedConditionals || [];

                        if (conditionalInput.type === 'radio' && nestedOptions.length > 0) {
                          return (
                            <Field key={`nested_field_${conditionalIndex}`} name={conditionalFieldName}>
                              {({ field: conditionalField }) => {
                                const isMainSelected = conditionalField.value === conditionalInput.label;

                                return isMainSelected && conditionalField.value === conditionalInput.label ? (
                                  <div style={{ marginLeft: '20px', marginTop: '10px' }} className={`grid-5 ${isAuthorizedContacts ? 'authorized-contacts-grid' : ''}`}>
                                    <Field name={`${conditionalFieldName}_nested`}>
                                      {({ field: nestedGroupField }) => {
                                        return nestedOptions.map((nestedOption, nestedIndex) => {
                                          const isNestedSelected = nestedGroupField.value === nestedOption.label;

                                          return (
                                            <label
                                              key={`nested_${nestedIndex}`}
                                              className={`radio-label gender-label ${isNestedSelected ? 'selected' : ''}`}
                                              style={{ marginBottom: '10px' }}
                                            >
                                              <input
                                                type="radio"
                                                name={`${conditionalFieldName}_nested`}
                                                value={nestedOption.label}
                                                checked={isNestedSelected}
                                                onChange={(e) => {
                                                  form.setFieldValue(`${conditionalFieldName}_nested`, e.target.value);
                                                }}
                                              />
                                              <span>{nestedOption.label}</span>
                                            </label>
                                          );
                                        });
                                      }}
                                    </Field>
                                  </div>
                                ) : null;
                              }}
                            </Field>
                          );
                        }

                        // Handle checkbox nested conditionals
                        if (conditionalInput.type === 'checkbox' && nestedOptions.length > 0) {
                          return (
                            <Field key={`nested_checkbox_field_${conditionalIndex}`} name={conditionalFieldName}>
                              {({ field: conditionalField }) => {
                                const isChecked = conditionalField.value === true || conditionalField.value === 'true';

                                return isChecked ? (
                                  <div className="nested-conditionals" style={{ marginTop: '10px', marginLeft: '20px' }}>
                                    <Field name={`${conditionalFieldName}_nested`}>
                                      {({ field: nestedGroupField }) => {
                                        return nestedOptions.map((nestedOption, nestedIndex) => {
                                          const isNestedSelected = nestedGroupField.value === nestedOption.label;
                                          return (
                                            <label
                                              key={`nested_${nestedIndex}`}
                                              className={`radio-label gender-label ${isNestedSelected ? 'selected' : ''}`}
                                              style={{ display: 'block', marginBottom: '5px' }}
                                            >
                                              <input
                                                type="radio"
                                                name={`${conditionalFieldName}_nested`}
                                                value={nestedOption.label}
                                                checked={isNestedSelected}
                                                onChange={(e) => {
                                                  form.setFieldValue(`${conditionalFieldName}_nested`, e.target.value);
                                                }}
                                              />
                                              <span>{nestedOption.label}</span>
                                            </label>
                                          );
                                        });
                                      }}
                                    </Field>
                                  </div>
                                ) : null;
                              }}
                            </Field>
                          );
                        }

                        return null;
                      });
                    }

                    return null;
                  });
                }}
              </Field>

              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

   case 'checkbox':
  if ((finalFieldLabel === 'T & C' || field_name === 'tc') && termsText) {
    // Parse HTML string and render links as React <a> elements
    const renderTermsWithLinks = (html) => {
      const parts = [];
      const linkRegex = /<a\s+[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
      let lastIndex = 0;
      let match;

      while ((match = linkRegex.exec(html)) !== null) {
        // Text before the link
        if (match.index > lastIndex) {
          const textBefore = html.substring(lastIndex, match.index).replace(/<[^>]*>/g, '');
          if (textBefore) parts.push(<span key={`t${lastIndex}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html.substring(lastIndex, match.index)) }} />);
        }
        // The link itself - as a REAL React <a> element
        const href = match[1];
        const linkText = match[2].replace(/<[^>]*>/g, '');
        parts.push(
          <a key={`a${match.index}`} href={href} target="_blank" rel="noopener noreferrer"
            style={{ color: '#2486c5', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); }}
          >{linkText}</a>
        );
        lastIndex = match.index + match[0].length;
      }
      // Remaining text after last link
      if (lastIndex < html.length) {
        parts.push(<span key={`t${lastIndex}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html.substring(lastIndex)) }} />);
      }
      return parts.length > 0 ? parts : <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
    };

    return (
      <div key={fieldKey} className="form-group terms-conditions">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Field name={field_name}>
            {({ field, form }) => (
              <input
                type="checkbox"
                className="checkbox-input"
                checked={field.value === true}
                onChange={(e) => {
                  form.setFieldValue(field_name, e.target.checked);
                }}
                style={{ marginTop: '4px', flexShrink: 0 }}
              />
            )}
          </Field>
          <div className="checkbox-text terms-text"
            style={{ flex: 1, fontSize: '14px', lineHeight: '1.6', color: '#1e293b' }}>
            {renderTermsWithLinks(termsText)}
          </div>
        </div>
        <ErrorMessage name={field_name} component="div" className="error-message" />
      </div>
    );
  }

  // SMS/Follow-up consent checkbox - center aligned
  const isSmsConsent = field_name?.toLowerCase().includes('sms') ||
                       field_name?.toLowerCase().includes('followup') ||
                       field_name?.toLowerCase().includes('follow_up') ||
                       field_name?.toLowerCase().includes('consent') ||
                       finalFieldLabel?.toLowerCase().includes('sms') ||
                       finalFieldLabel?.toLowerCase().includes('follow up') ||
                       finalFieldLabel?.toLowerCase().includes('communications');

  if (isSmsConsent && (!field_options || field_options.length === 0)) {
    const rawSmsText = termsText || finalFieldLabel || field_name;
    const hasLinks = rawSmsText && rawSmsText.includes('<a ');

    // Parse links — merge same-line prefix text into the <a> for bigger touch target
    const renderSmsWithLinks = (html) => {
      const parts = [];
      const linkRegex = /<a\s+[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
      let lastIndex = 0;
      let match;
      while ((match = linkRegex.exec(html)) !== null) {
        const textBefore = html.substring(lastIndex, match.index);
        const href = match[1];
        const linkText = match[2].replace(/<[^>]*>/g, '');

        // Find last line break to split: text before break = normal, text after break = merge into link
        const lastBreak = Math.max(textBefore.lastIndexOf('<br'), textBefore.lastIndexOf('\n'));
        if (lastBreak >= 0) {
          const beforeBreak = textBefore.substring(0, lastBreak);
          if (beforeBreak) parts.push(<span key={`s${lastIndex}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(beforeBreak) }} />);
          // Prefix on same line as link (e.g. "View our ") — merge into link
          const prefix = textBefore.substring(lastBreak).replace(/<br\s*\/?>/gi, '').replace(/<[^>]*>/g, '');
          parts.push(<br key={`br${match.index}`} />);
          parts.push(
            <a key={`l${match.index}`} href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: '#2486c5', textDecoration: 'underline', cursor: 'pointer', display: 'inline-block', minHeight: '44px', lineHeight: '44px' }}
              onClick={(e) => { e.stopPropagation(); }}
            >{prefix}{linkText}</a>
          );
        } else {
          // No line break — render prefix as normal text, link separately
          if (textBefore) parts.push(<span key={`s${lastIndex}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textBefore) }} />);
          parts.push(
            <a key={`l${match.index}`} href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: '#2486c5', textDecoration: 'underline', cursor: 'pointer', display: 'inline-block', minHeight: '44px', lineHeight: '44px' }}
              onClick={(e) => { e.stopPropagation(); }}
            >{linkText}</a>
          );
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < html.length) {
        parts.push(<span key={`s${lastIndex}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html.substring(lastIndex)) }} />);
      }
      return parts.length > 0 ? parts : html;
    };

    return (
      <div key={fieldKey} className="form-group terms-conditions">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Field name={field_name}>
            {({ field, form }) => (
              <input
                type="checkbox"
                className="checkbox-input"
                checked={field.value === true}
                onChange={(e) => {
                  form.setFieldValue(field_name, e.target.checked);
                }}
                style={{ marginTop: '4px', flexShrink: 0 }}
              />
            )}
          </Field>
          <div className="checkbox-text terms-text"
            style={{ flex: 1, fontSize: '14px', lineHeight: '1.6', color: '#1e293b' }}>
            {hasLinks ? renderSmsWithLinks(rawSmsText) : (
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rawSmsText) }} />
            )}
          </div>
        </div>
        <ErrorMessage name={field_name} component="div" className="error-message" />
      </div>
    );
  }

  return (
    <div key={fieldKey} className="form-group">
      {!field.hideLabel && (
        <label className="form-label label-txt">
          {finalFieldLabel || field_name} {field.required && <span className="required">*</span>}
        </label>
      )}
      <div className={`grid-5 ${isAuthorizedContacts ? 'authorized-contacts-grid' : ''}`}>
        {field_options && Array.isArray(field_options) && field_options.length > 0 ? (
          field_options.map((option, optionIndex) => {
            if (option === null || option === undefined) return null;
            const optionValue = typeof option === 'object' && option !== null ? option.value : String(option);
            const optionLabel = typeof option === 'object' && option !== null ? option.label : String(option);
            const isOtherOption = optionLabel.toLowerCase().includes('other') || optionValue.toLowerCase().includes('other');
            const isSelected = Array.isArray(values[field_name])
              ? isOtherOption
                ? values[field_name].some(val => val === optionValue || val.startsWith(`${optionValue} - `))
                : values[field_name].includes(optionValue)
              : values[field_name] === optionValue;

            // Enhanced matching: try exact match first, then case-insensitive
            let conditionalOption = field.optionsWithConditional?.find(
              opt => opt.label === optionLabel || opt.label === optionValue
            );
            // Fallback: case-insensitive matching
            if (!conditionalOption && field.optionsWithConditional) {
              conditionalOption = field.optionsWithConditional.find(
                opt => opt.label?.toLowerCase()?.trim() === optionLabel?.toLowerCase()?.trim() ||
                       opt.label?.toLowerCase()?.trim() === optionValue?.toLowerCase()?.trim()
              );
            }
            const conditionalInputs = conditionalOption?.conditionalInputs || [];
            // Find actual config index to match FormPage.jsx initialization
            const configIndex = field.optionsWithConditional?.findIndex(cfg => cfg === conditionalOption) ?? optionIndex;

            return (
              <React.Fragment key={`${fieldKey}_checkbox_${optionIndex}`}>
                {/* Checkbox Option */}
                <label
                  className={`checkbox-label gender-label ${isSelected ? 'selected' : ''}`}
                >
                  <Field name={field_name}>
                    {({ field, form }) => {
                      // For "Other" options, check if any value starts with "Other - " or equals "Other"
                      const currentValues = Array.isArray(field.value) ? field.value : [];
                      const isChecked = isOtherOption
                        ? currentValues.some(val => val === optionValue || val.startsWith(`${optionValue} - `))
                        : currentValues.includes(optionValue);

                      // Check if this is a "skip to verification" type checkbox
                      const isSkipOption = optionLabel.toLowerCase().includes('skip') ||
                                          optionLabel.toLowerCase().includes('verification') ||
                                          optionLabel.toLowerCase().includes('no known');

                      return (
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          value={optionValue}
                          checked={isChecked}
                          onChange={(e) => {
                            let newValues;

                            if (e.target.checked) {
                              // Add the option
                              newValues = [...currentValues, optionValue];

                              // If this is a skip option, scroll to Next button
                              if (isSkipOption) {
                                setTimeout(() => {
                                  const nextButton = document.querySelector('.btn.next');
                                  if (nextButton) {
                                    nextButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }
                                }, 100);
                              }
                            } else {
                              if (isOtherOption) {
                                newValues = currentValues.filter(val =>
                                  val !== optionValue && !val.startsWith(`${optionValue} - `)
                                );

                                if (conditionalInputs.length > 0) {
                                  conditionalInputs.forEach((conditionalField, conditionalIndex) => {
                                    const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                                    form.setFieldValue(conditionalFieldName, '');
                                  });
                                }

                                const arrayFieldName = `${field_name}${optionValue.toLowerCase()}${Date.now()}`;
                                const existingArrayField = Object.keys(form.values).find(key =>
                                  key.startsWith(`${field_name}${optionValue.toLowerCase()}`)
                                );
                                if (existingArrayField) {
                                  form.setFieldValue(existingArrayField, []);
                                }
                              } else {
                                newValues = currentValues.filter(val => val !== optionValue);
                              }
                            }
                            form.setFieldValue(field_name, newValues);
                          }}
                        />
                      );
                    }}
                  </Field>
                  <span className="checkbox-text">{optionLabel}</span>
                </label>

                {isSelected && conditionalInputs.length > 0 && (
                  <div
                    className={isAuthorizedContacts ? "authorized-conditional-fields" : "conditional-fields-container"}
                    data-field-count={conditionalInputs.length}
                  >
                    {conditionalInputs.map((conditionalField, conditionalIndex) => {
                      const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                      const isTextarea = conditionalField.type === 'textarea';
                      const isRadioOrCheckbox = conditionalField.type === 'radio' || conditionalField.type === 'checkbox';

                      // Radio group ke liye common name
                      const radioGroupName = `${field_name}_conditional_${configIndex}_radio_group`;
                      
                      return (
                        <div
                          key={`conditional_${conditionalIndex}`}
                          className={`form-group conditional-field ${isTextarea ? 'textarea-field' : ''} ${isRadioOrCheckbox ? 'radio-checkbox-field' : ''}`}
                        >
                          {!isRadioOrCheckbox && (
                            <label className="form-label">
                              {conditionalField.label} {conditionalField.required && <span className="required">*</span>}
                            </label>
                          )}
                          <Field name={conditionalField.type === 'radio' ? radioGroupName : conditionalFieldName}>
                            {({ field: conditionalFieldProps, form }) => {
                              // Render based on field type
                              if (conditionalField.type === 'textarea') {
                                return (
                                  <textarea
                                    {...conditionalFieldProps}
                                    className="form-control"
                                    placeholder={conditionalField.placeholder || 'Enter details...'}
                                    rows="3"
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      form.setFieldValue(conditionalFieldName, inputValue);

                                      if (isOtherOption && inputValue.trim()) {
                                        const currentMainValues = Array.isArray(form.values[field_name])
                                          ? form.values[field_name]
                                          : [];
                                        const updatedValues = currentMainValues.map((val) =>
                                          val === optionValue ? `${optionValue} - ${inputValue}` : val
                                        );

                                        if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                          form.setFieldValue(field_name, updatedValues);
                                        }
                                      }
                                    }}
                                  />
                                );
                              } else if (conditionalField.type === 'radio') {
                                const radioValue = conditionalField.value || conditionalField.label;
                                const isRadioSelected = conditionalFieldProps.value === radioValue;
                                
                                return (
                                  <label className={`radio-label gender-label ${isRadioSelected ? 'selected' : ''}`}>
                                    <input
                                      type="radio"
                                      name={radioGroupName}
                                      className="radio-input"
                                      value={radioValue}
                                      checked={isRadioSelected}
                                      onChange={(e) => {
                                        const inputValue = e.target.value;
                                        form.setFieldValue(radioGroupName, inputValue);

                                        if (isOtherOption && inputValue.trim()) {
                                          const currentMainValues = Array.isArray(form.values[field_name])
                                            ? form.values[field_name]
                                            : [];
                                          const updatedValues = currentMainValues.map((val) =>
                                            val === optionValue ? `${optionValue} - ${inputValue}` : val
                                          );

                                          if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                            form.setFieldValue(field_name, updatedValues);
                                          }
                                        }
                                      }}
                                    />
                                    <span className="radio-text">{conditionalField.label}</span>
                                  </label>
                                );
                              } else if (conditionalField.type === 'checkbox') {
                                return (
                                  <label className={`checkbox-label gender-label ${conditionalFieldProps.value === true ? 'selected' : ''}`}>
                                    <input
                                      type="checkbox"
                                      className="checkbox-input"
                                      checked={conditionalFieldProps.value === true}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        form.setFieldValue(conditionalFieldName, isChecked);

                                        if (isOtherOption && isChecked) {
                                          const currentMainValues = Array.isArray(form.values[field_name])
                                            ? form.values[field_name]
                                            : [];
                                          const updatedValues = currentMainValues.map((val) =>
                                            val === optionValue ? `${optionValue} - ${conditionalField.label}` : val
                                          );

                                          if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                            form.setFieldValue(field_name, updatedValues);
                                          }
                                        }
                                      }}
                                    />
                                    <span className="checkbox-text">{conditionalField.label}</span>
                                  </label>
                                );
                              } else if (conditionalField.type === 'select' || conditionalField.type === 'dropdown') {
                                return (
                                  <select
                                    {...conditionalFieldProps}
                                    className="form-control"
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      form.setFieldValue(conditionalFieldName, inputValue);

                                      if (isOtherOption && inputValue.trim()) {
                                        const currentMainValues = Array.isArray(form.values[field_name])
                                          ? form.values[field_name]
                                          : [];
                                        const updatedValues = currentMainValues.map((val) =>
                                          val === optionValue ? `${optionValue} - ${inputValue}` : val
                                        );

                                        if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                          form.setFieldValue(field_name, updatedValues);
                                        }
                                      }
                                    }}
                                  >
                                    <option value="">Select...</option>
                                    {conditionalField.options?.map((opt, idx) => (
                                      <option key={idx} value={opt.value || opt}>
                                        {opt.label || opt}
                                      </option>
                                    ))}
                                  </select>
                                );
                              } else if (conditionalField.type === 'file') {
                                return (
                                  <input
                                    type="file"
                                    className="form-control"
                                    accept={conditionalField.accept || '*'}
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        form.setFieldValue(conditionalFieldName, file);

                                        if (isOtherOption) {
                                          const currentMainValues = Array.isArray(form.values[field_name])
                                            ? form.values[field_name]
                                            : [];
                                          const updatedValues = currentMainValues.map((val) =>
                                            val === optionValue ? `${optionValue} - ${file.name}` : val
                                          );

                                          if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                            form.setFieldValue(field_name, updatedValues);
                                          }
                                        }
                                      }
                                    }}
                                  />
                                );
                              } else {
                                return (
                                  <input
                                    {...conditionalFieldProps}
                                    type={conditionalField.type || 'text'}
                                    className="form-control"
                                    placeholder={conditionalField.placeholder || ''}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      form.setFieldValue(conditionalFieldName, inputValue);

                                      if (isOtherOption && inputValue.trim()) {
                                        const currentMainValues = Array.isArray(form.values[field_name])
                                          ? form.values[field_name]
                                          : [];
                                        const updatedValues = currentMainValues.map((val) =>
                                          val === optionValue ? `${optionValue} - ${inputValue}` : val
                                        );

                                        if (JSON.stringify(updatedValues) !== JSON.stringify(currentMainValues)) {
                                          form.setFieldValue(field_name, updatedValues);
                                        }
                                      }
                                    }}
                                  />
                                );
                              }
                            }}
                          </Field>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <label className={`checkbox-label gender-label ${values[field_name] ? 'selected' : ''}`}>
            <Field name={field_name}>
              {({ field, form }) => (
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={field.value === true}
                  onChange={(e) => {
                    form.setFieldValue(field_name, e.target.checked);
                  }}
                />
              )}
            </Field>
            <span className="checkbox-text">{finalFieldLabel || field_name}</span>
          </label>
        )}
      </div>
      <ErrorMessage name={field_name} component="div" className="error-message" />
    </div>
  );
  
  case 'ros':
  return (
    <div key={fieldKey} className="form-group">
      {!field.hideLabel && (
        <label className="form-label label-txt">
          {finalFieldLabel || field_name} {field.required && <span className="required">*</span>}
        </label>
      )}
      <div className="checkbox-group grid-3">
        {field_options && Array.isArray(field_options) && field_options.length > 0 ? (
          field_options.map((option, optionIndex) => {
            if (option === null || option === undefined) return null;
            const optionValue = typeof option === 'object' && option !== null ? option.value : String(option);
            const optionLabel = typeof option === 'object' && option !== null ? option.label : String(option);
            const isOtherOption = optionLabel.toLowerCase().includes('other') || optionValue.toLowerCase().includes('other');

            const currentValues = Array.isArray(values[field_name]) ? values[field_name] : [];
            const isSelected = isOtherOption
              ? currentValues.some(val => val === optionValue || val.startsWith(`${optionValue} - `))
              : currentValues.includes(optionValue);

            // Enhanced matching: try exact match first, then case-insensitive
            let conditionalOption = field.optionsWithConditional?.find(
              opt => opt.label === optionLabel || opt.label === optionValue
            );
            // Fallback: case-insensitive matching
            if (!conditionalOption && field.optionsWithConditional) {
              conditionalOption = field.optionsWithConditional.find(
                opt => opt.label?.toLowerCase()?.trim() === optionLabel?.toLowerCase()?.trim() ||
                       opt.label?.toLowerCase()?.trim() === optionValue?.toLowerCase()?.trim()
              );
            }
            const conditionalInputs = conditionalOption?.conditionalInputs || [];
            // Find actual config index to match FormPage.jsx initialization
            const configIndex = field.optionsWithConditional?.findIndex(cfg => cfg === conditionalOption) ?? optionIndex;

            return (
              <React.Fragment key={`${fieldKey}_checkbox_${optionIndex}`}>
                {/* Main Checkbox */}
                <div className="checkbox-option">
                  <label
                    className={`checkbox-label gender-label ${isSelected ? 'selected' : ''}`}
                  >
                    <Field name={field_name}>
                      {({ field, form }) => {
                        const currentVals = Array.isArray(field.value) ? field.value : [];
                        const isChecked = isOtherOption
                          ? currentVals.some(val => val === optionValue || val.startsWith(`${optionValue} - `))
                          : currentVals.includes(optionValue);

                        return (
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            value={optionValue}
                            checked={isChecked}
                            onChange={(e) => {
                              let newValues;

                              if (e.target.checked) {
                                newValues = [...currentVals, optionValue];
                              } else {
                                if (isOtherOption) {
                                  newValues = currentVals.filter(val =>
                                    val !== optionValue && !val.startsWith(`${optionValue} - `)
                                  );
                                } else {
                                  newValues = currentVals.filter(val => val !== optionValue);
                                }

                                // Clear conditional fields
                                if (conditionalInputs.length > 0) {
                                  conditionalInputs.forEach((conditionalField, conditionalIndex) => {
                                    const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                                    form.setFieldValue(conditionalFieldName, false);
                                  });
                                }
                              }
                              form.setFieldValue(field_name, newValues);
                            }}
                          />
                        );
                      }}
                    </Field>
                    <span className="checkbox-text">{optionLabel}</span>
                  </label>
                </div>

                {/* Conditional Fields - checkboxes in grid cells */}
                {isSelected && conditionalInputs.length > 0 && (
                  <div className={isAuthorizedContacts ? "authorized-conditional-fields" : "conditional-fields-container"}>
                    {/* Conditional checkboxes */}
                    {conditionalInputs.map((conditionalField, conditionalIndex) => {
                      const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                      const nestedInputs = conditionalField.nestedInputs || [];

                      return (
                        <div
                          key={`conditional_${conditionalIndex}`}
                          className="form-group conditional-field"
                        >
                          <Field name={conditionalFieldName}>
                            {({ field: conditionalFieldProps, form }) => (
                              <label className={`checkbox-label gender-label ${conditionalFieldProps.value ? 'selected' : ''}`}>
                                <input
                                  type="checkbox"
                                  className="checkbox-input"
                                  checked={conditionalFieldProps.value === true}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    form.setFieldValue(conditionalFieldName, isChecked);

                                    if (!isChecked && nestedInputs.length > 0) {
                                      nestedInputs.forEach((nested, nestedIdx) => {
                                        const nestedFieldName = `${conditionalFieldName}_nested_${nested.id || nestedIdx}`;
                                        form.setFieldValue(nestedFieldName, '');
                                      });
                                    }

                                    setTimeout(() => {
                                      updateROSFieldWithConditionals(form, field_name, optionValue, conditionalInputs, configIndex);
                                    }, 0);
                                  }}
                                />
                                <span className="checkbox-text">
                                  {conditionalField.label} {conditionalField.required && <span className="required">*</span>}
                                </span>
                              </label>
                            )}
                          </Field>
                        </div>
                      );
                    })}

                    {/* Nested inputs - only show when conditional checkbox is checked */}
                    {conditionalInputs.map((conditionalField, conditionalIndex) => {
                      const conditionalFieldName = `${field_name}_conditional_${configIndex}_${conditionalIndex}`;
                      const nestedInputs = conditionalField.nestedInputs || [];

                      if (nestedInputs.length === 0) return null;

                      return (
                        <Field key={`nested_wrapper_${conditionalIndex}`} name={conditionalFieldName}>
                          {({ field: parentField }) => {
                            // Only show nested inputs when parent conditional checkbox is checked
                            if (!parentField.value) return null;

                            return (
                              <div
                                style={{
                                  gridColumn: '1 / -1',
                                  width: '100%',
                                  marginTop: '10px',
                                  padding: '12px',
                                  backgroundColor: '#fff',
                                  borderRadius: '8px',
                                  border: '1px solid #e9ecef'
                                }}
                              >
                                {nestedInputs.map((nestedInput, nestedIndex) => {
                                  const nestedFieldName = `${conditionalFieldName}_nested_${nestedInput.id || nestedIndex}`;
                                  return (
                                    <div key={nestedFieldName} style={{ marginBottom: nestedIndex < nestedInputs.length - 1 ? '10px' : '0' }}>
                                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                                        {nestedInput.label} {nestedInput.required && <span className="required">*</span>}
                                      </label>
                                      <Field name={nestedFieldName}>
                                        {({ field: nestedFieldProps, form: nestedForm }) => {
                                          if (nestedInput.type === 'textarea') {
                                            return (
                                              <textarea
                                                {...nestedFieldProps}
                                                placeholder={nestedInput.placeholder}
                                                style={{
                                                  width: '100%',
                                                  padding: '10px',
                                                  fontSize: '14px',
                                                  border: '1px solid #ced4da',
                                                  borderRadius: '6px',
                                                  minHeight: '70px',
                                                  resize: 'vertical',
                                                  backgroundColor: '#fff'
                                                }}
                                              />
                                            );
                                          }
                                          if (nestedInput.type === 'number') {
                                            const currentVal = nestedFieldProps.value !== undefined && nestedFieldProps.value !== '' ? Number(nestedFieldProps.value) : 0;
                                            return (
                                              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                                <input
                                                  type="number"
                                                  {...nestedFieldProps}
                                                  placeholder={nestedInput.placeholder}
                                                  style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    paddingRight: '35px',
                                                    fontSize: '14px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fff'
                                                  }}
                                                />
                                                <div style={{
                                                  position: 'absolute',
                                                  right: '1px',
                                                  top: '1px',
                                                  bottom: '1px',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  borderLeft: '1px solid #ced4da',
                                                  background: '#f8f9fa',
                                                  borderRadius: '0 5px 5px 0'
                                                }}>
                                                  <button
                                                    type="button"
                                                    onClick={() => nestedForm.setFieldValue(nestedFieldName, currentVal + 1)}
                                                    style={{
                                                      flex: 1,
                                                      border: 'none',
                                                      background: 'transparent',
                                                      cursor: 'pointer',
                                                      padding: '0 8px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      borderBottom: '1px solid #ced4da',
                                                      fontSize: '10px',
                                                      color: '#495057'
                                                    }}
                                                  >
                                                    ▲
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => nestedForm.setFieldValue(nestedFieldName, currentVal - 1)}
                                                    style={{
                                                      flex: 1,
                                                      border: 'none',
                                                      background: 'transparent',
                                                      cursor: 'pointer',
                                                      padding: '0 8px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      fontSize: '10px',
                                                      color: '#495057'
                                                    }}
                                                  >
                                                    ▼
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          }
                                          return (
                                            <input
                                              type={nestedInput.type || 'text'}
                                              {...nestedFieldProps}
                                              placeholder={nestedInput.placeholder}
                                              style={{
                                                width: '100%',
                                                padding: '10px',
                                                fontSize: '14px',
                                                border: '1px solid #ced4da',
                                                borderRadius: '6px',
                                                backgroundColor: '#fff'
                                              }}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }}
                        </Field>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <label className={`checkbox-label ${values[field_name] ? 'active selected' : ''}`}>
            <Field name={field_name}>
              {({ field, form }) => (
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={field.value === true}
                  onChange={(e) => {
                    form.setFieldValue(field_name, e.target.checked);
                  }}
                />
              )}
            </Field>
            <span className="checkbox-text">{finalFieldLabel || field_name}</span>
          </label>
        )}
      </div>
      <ErrorMessage name={field_name} component="div" className="error-message" />
    </div>
  );

        case 'accordion':
          return (
            <div key={fieldKey} className="form-group accordion-field">
              {!field.hideLabel && (
                <label className="form-label label-txt">
                  {finalFieldLabel || field_name} {field.required && <span className="required">*</span>}
                </label>
              )}
              <Field name={field_name}>
                {({ form }) => (
                  <div className="accordion-container">
                    {field.accordionSections?.map((section, sectionIndex) => {
                      const sectionFieldName = `${field_name}_section_${sectionIndex}`;

                      return (
                        <AccordionSection
                          key={section.id || sectionIndex}
                          section={section}
                          sectionIndex={sectionIndex}
                          sectionFieldName={sectionFieldName}
                          formikValues={form.values}
                        />
                      );
                    })}
                  </div>
                )}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

        case 'date':
          return (
            <div key={fieldKey} className="form-group">
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {finalFieldLabel || field_name}
                  {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field name={field_name}>
                {({ field, form }) => (
                  <MultiStepDatePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(val) => {
                      form.setFieldValue(field_name, val);
                    }}
                    placeholder="Select Date of Birth"
                    className={form.errors[field_name] && form.touched[field_name] ? "error-input" : ""}
                    maxDate={new Date()}
                  />
                )}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

        case 'calendar': {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          return (
            <div key={fieldKey} className="form-group">
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field name={field_name}>
                {({ field: formField, form }) => (
                  <DatePicker
                    selected={formField.value ? new Date(formField.value) : null}
                    onChange={(val) => {
                      form.setFieldValue(field_name, val);
                    }}
                    dateFormat="MM-dd-yyyy"
                    className={form.errors[field_name] && form.touched[field_name] ? "error-input" : ""}
                    placeholderText="MM-DD-YYYY"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    maxDate={today}
                    isClearable
                  />
                )}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
        }

        case 'number': {
          // ✅ Check if this is a ZIP/Postal code field - should be text input without spinner
          const isZipField = field_name?.toLowerCase()?.includes('zip') ||
                            field_name?.toLowerCase()?.includes('postal') ||
                            field_name?.toLowerCase()?.includes('pincode') ||
                            finalFieldLabel?.toLowerCase()?.includes('zip') ||
                            finalFieldLabel?.toLowerCase()?.includes('postal') ||
                            finalFieldLabel?.toLowerCase()?.includes('pin code');

          // ✅ ZIP Code: Render as text input without spinner
          if (isZipField) {
            return (
              <div key={fieldKey} className="form-group" style={{ width: isMobile ? '100%' : `${field.fieldWidth}%` }}>
                {!field.hideLabel && (
                  <label htmlFor={field_name} className='label-txt'>
                    {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                  </label>
                )}
                <Field
                  type="text"
                  name={field_name}
                  id={field_name}
                  placeholder={field_placeholder || 'Enter ZIP code'}
                  maxLength="10"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onInput={(e) => {
                    // Allow only numbers and limit to valid ZIP format
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  }}
                  className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
                />
                <ErrorMessage name={field_name} component="div" className="error-message" />
              </div>
            );
          }

          // ✅ Regular number field with spinner
          return (
            <div key={fieldKey} className="form-group" style={{ width: isMobile ? '100%' : `${field.fieldWidth}%` }}>
              {!field.hideLabel && (
                <label htmlFor={field_name} className='label-txt'>
                  {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                </label>
              )}
              <Field name={field_name}>
                {({ field: formikField, form }) => {
                  const currentVal = formikField.value !== undefined && formikField.value !== '' ? Number(formikField.value) : 0;
                  return (
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                      <input
                        type="number"
                        {...formikField}
                        id={field_name}
                        placeholder={field_placeholder || ''}
                        className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
                        style={{ paddingRight: '35px' }}
                      />
                      <div style={{
                        position: 'absolute',
                        right: '1px',
                        top: '1px',
                        bottom: '1px',
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: '1px solid #ced4da',
                        background: '#f8f9fa',
                        borderRadius: '0 4px 4px 0',
                        margin: '2px'
                      }}>
                        <button
                          type="button"
                          onClick={() => form.setFieldValue(field_name, currentVal + 1)}
                          style={{
                            flex: 1,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: '1px solid #ced4da',
                            fontSize: '10px',
                            color: '#495057'
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          onClick={() => form.setFieldValue(field_name, currentVal - 1)}
                          style={{
                            flex: 1,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: '#495057'
                          }}
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  );
                }}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
        }

        case 'range': {
          const min = minValue !== undefined ? minValue : 0;
          const max = maxValue !== undefined ? maxValue : 10;
          const step = stepValue !== undefined ? stepValue : 1;
          const defaultVal = defaultValue !== undefined ? defaultValue : min;

          // Pain level helper functions
          const getPainLevelInfo = (value) => {
            if (value === 0) return { label: 'No Pain', color: '#28a745', bgColor: '#d4edda' };
            if (value >= 1 && value <= 3) return { label: 'Mild', color: '#17a2b8', bgColor: '#d1ecf1' };
            if (value >= 4 && value <= 6) return { label: 'Moderate', color: '#fd7e14', bgColor: '#fff3cd' };
            return { label: 'Severe', color: '#dc3545', bgColor: '#f8d7da' };
          };

          const getTrackColor = (value) => {
            if (value === 0) return '#28a745';
            if (value >= 1 && value <= 3) return '#17a2b8';
            if (value >= 4 && value <= 6) return '#fd7e14';
            return '#dc3545';
          };

          return (
            <div key={fieldKey} className="form-group pain-scale-container" style={{ width: isMobile ? '100%' : `${field.fieldWidth}%` }}>
              <Field name={field_name}>
                {({ field, form }) => {
                  const hasValue = field.value !== undefined && field.value !== null && field.value !== '';
                  const currentValue = hasValue ? Number(field.value) : defaultVal;
                  const painInfo = getPainLevelInfo(currentValue);
                  const trackColor = getTrackColor(currentValue);
                  const percentage = ((currentValue - min) / (max - min)) * 100;

                  return (
                    <div className="pain-scale-card" style={{
                      background: '#fff',
                      borderRadius: '12px',
                      padding: isMobile ? '16px' : '20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: '1px solid #e9ecef'
                    }}>
                      {/* Header with label and value */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        {!field.hideLabel && (
                          <label htmlFor={field_name} style={{
                            fontWeight: '600',
                            fontSize: isMobile ? '14px' : '16px',
                            color: '#333',
                            margin: 0,
                            textTransform: 'uppercase'
                          }}>
                            {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
                          </label>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            fontSize: isMobile ? '28px' : '36px',
                            fontWeight: '700',
                            color: trackColor
                          }}>
                            {currentValue}
                          </span>
                          <span style={{
                            background: painInfo.bgColor,
                            color: painInfo.color,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '600'
                          }}>
                            {painInfo.label}
                          </span>
                        </div>
                      </div>

                      {/* Slider track */}
                      <div style={{
                        position: 'relative',
                        height: '8px',
                        background: '#e9ecef',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}>
                        {/* Filled portion */}
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${percentage}%`,
                          background: trackColor,
                          borderRadius: '4px',
                          transition: 'width 0.15s ease, background 0.15s ease'
                        }} />

                        {/* Thumb */}
                        <div style={{
                          position: 'absolute',
                          left: `${percentage}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '20px',
                          height: '20px',
                          background: trackColor,
                          borderRadius: '50%',
                          border: '3px solid #fff',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                          transition: 'left 0.15s ease, background 0.15s ease'
                        }} />

                        {/* Invisible range input for interaction */}
                        <input
                          type="range"
                          id={field_name}
                          min={min}
                          max={max}
                          step={step}
                          value={currentValue}
                          onChange={(e) => {
                            const newValue = Number(e.target.value);
                            form.setFieldValue(field_name, newValue);
                            if (setPainLevels) {
                              const fn = field_name.toLowerCase();
                              if (fn.includes('currentpain')) setPainLevels(prev => ({ ...prev, current: newValue }));
                              else if (fn.includes('bestpain')) setPainLevels(prev => ({ ...prev, best: newValue }));
                              else if (fn.includes('worstpain')) setPainLevels(prev => ({ ...prev, worst: newValue }));
                            }
                          }}
                          onMouseDown={() => {
                            // Set initial value on first click if not set
                            if (!hasValue) {
                              form.setFieldValue(field_name, currentValue);
                            }
                          }}
                          onTouchStart={() => {
                            // Set initial value on first touch if not set
                            if (!hasValue) {
                              form.setFieldValue(field_name, currentValue);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '-10px',
                            left: 0,
                            width: '100%',
                            height: '50px',
                            opacity: 0,
                            cursor: 'pointer',
                            margin: 0
                          }}
                        />
                      </div>

                      {/* Scale labels */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '12px',
                        padding: '0 2px'
                      }}>
                        {Array.from({ length: max - min + 1 }, (_, i) => {
                          const value = min + i;
                          let displayLabel = value.toString();

                          // Show NP for 0 and S for max
                          if (value === 0) displayLabel = 'NP';
                          else if (value === max) displayLabel = 'S';

                          const isActive = value === currentValue;

                          return (
                            <span
                              key={`${fieldKey}_scale_${value}`}
                              onClick={() => {
                                form.setFieldValue(field_name, value);
                              }}
                              style={{
                                fontSize: isMobile ? '11px' : '13px',
                                color: isActive ? trackColor : '#6c757d',
                                fontWeight: isActive ? '700' : '500',
                                cursor: 'pointer',
                                transition: 'color 0.15s ease',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}
                            >
                              {displayLabel}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
        }
        
        case 'logo':
        case 'file':
        case 'image':
        case 'qrcode': {
          // Check if this is a document upload field (DL, Insurance)
          const docType = field.docType;
          const isDocumentUpload = docType && (
            docType.includes('dl_') ||
            docType.includes('primary_') ||
            docType.includes('secondary_') ||
            docType.includes('tertiary_')
          );

          // Get icon and colors based on document type
          const getDocTypeInfo = (type) => {
            if (type?.includes('dl_')) {
              return {
                icon: '🪪 ',
                color: '#3b82f6',
                bgColor: '#eff6ff',
                borderColor: '#bfdbfe',
                title: type.includes('front') ? "Driver's License - Front" : "Driver's License - Back"
              };
            }
            if (type?.includes('primary_')) {
              return {
                icon: '🏥',
                color: '#10b981',
                bgColor: '#ecfdf5',
                borderColor: '#a7f3d0',
                title: type.includes('front') ? 'Primary Insurance - Front' : 'Primary Insurance - Back'
              };
            }
            if (type?.includes('secondary_')) {
              return {
                icon: '🏥',
                color: '#f59e0b',
                bgColor: '#fffbeb',
                borderColor: '#fde68a',
                title: type.includes('front') ? 'Secondary Insurance - Front' : 'Secondary Insurance - Back'
              };
            }
            if (type?.includes('tertiary_')) {
              return {
                icon: '🏥',
                color: '#8b5cf6',
                bgColor: '#f5f3ff',
                borderColor: '#c4b5fd',
                title: type.includes('front') ? 'Tertiary Insurance - Front' : 'Tertiary Insurance - Back'
              };
            }
            return null;
          };

          const docInfo = getDocTypeInfo(docType);

          if (docInfo) {
            const isDL = docType?.includes('dl_');

            // SVG Card Components for DL - Responsive size with pointerEvents none
            const DLFrontCard = () => (
              <svg viewBox="0 0 280 175" fill="none" style={{ width: '100%', height: 'auto', maxWidth: '100%', pointerEvents: 'none' }}>
                <rect width="280" height="175" rx="12" fill="#1a5f7a"/>
                <text x="140" y="30" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="600">DRIVER'S LICENSE</text>
                <rect x="20" y="50" width="70" height="85" rx="6" fill="#fff"/>
                <circle cx="55" cy="75" r="20" fill="#b8d4e3"/>
                <path d="M30 115 Q55 95 80 115" fill="#4a90a4"/>
                <rect x="105" y="55" width="155" height="12" rx="6" fill="#4a90a4"/>
                <rect x="105" y="78" width="130" height="12" rx="6" fill="#4a90a4"/>
                <rect x="105" y="101" width="100" height="12" rx="6" fill="#4a90a4"/>
                <rect x="105" y="124" width="120" height="12" rx="6" fill="#4a90a4"/>
              </svg>
            );

            const DLBackCard = () => (
              <svg viewBox="0 0 280 175" fill="none" style={{ width: '100%', height: 'auto', maxWidth: '100%', pointerEvents: 'none' }}>
                <rect width="280" height="175" rx="12" fill="#1a5f7a"/>
                <rect x="0" y="25" width="280" height="40" fill="#0d3d4d"/>
                <rect x="60" y="90" width="160" height="60" fill="#0d3d4d"/>
                {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30].map(i => (
                  <rect key={i} x={70 + i * 4.8} y="100" width={i % 3 === 0 ? 3 : 2} height="40" fill="#8ab4c4"/>
                ))}
              </svg>
            );

            // Corner brackets component for DL - pointerEvents none so clicks pass through
            const CornerBrackets = () => (
              <>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '35px', height: '35px', borderTop: '4px solid #1a5f7a', borderLeft: '4px solid #1a5f7a', borderTopLeftRadius: '6px', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 0, right: 0, width: '35px', height: '35px', borderTop: '4px solid #1a5f7a', borderRight: '4px solid #1a5f7a', borderTopRightRadius: '6px', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: '35px', height: '35px', borderBottom: '4px solid #1a5f7a', borderLeft: '4px solid #1a5f7a', borderBottomLeftRadius: '6px', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: '35px', height: '35px', borderBottom: '4px solid #1a5f7a', borderRight: '4px solid #1a5f7a', borderBottomRightRadius: '6px', pointerEvents: 'none' }} />
              </>
            );

            return (
              <div key={fieldKey} className="form-group" style={{ width: isMobile ? '100%' : '48%' }}>
                <Field name={field_name}>
                  {({ field: formikField, form }) => {
                    // Detect iOS device
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

                    const handleFileChange = (e) => {
                      logger.log(`File change event triggered for ${docInfo.title}`);
                      const file = e.target.files?.[0];

                      if (!file) {
                        logger.log('No file selected');
                        return;
                      }

                      logger.log(`File selected - Name: ${file.name}, Type: ${file.type}, Size: ${file.size} bytes`);

                      // Validate file type - iOS sometimes returns empty type
                      const isValidType = file.type.startsWith('image/') ||
                                         file.name.match(/\.(jpg|jpeg|png|gif|webp|heic)$/i);

                      if (!isValidType) {
                        alert('Please select a valid image file (JPG, PNG, etc.)');
                        e.target.value = '';
                        return;
                      }

                      // Validate file size (max 10MB)
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      if (file.size > maxSize) {
                        alert('Image size is too large. Please select an image smaller than 10MB.');
                        e.target.value = '';
                        return;
                      }

                      logger.log(`Processing ${docInfo.title}: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);

                      const reader = new FileReader();

                      reader.onloadstart = () => {
                        logger.log(`Started reading ${docInfo.title}`);
                      };

                      reader.onprogress = (event) => {
                        if (event.lengthComputable) {
                          const percent = (event.loaded / event.total) * 100;
                          logger.log(`Reading ${docInfo.title}: ${percent.toFixed(0)}%`);
                        }
                      };

                      reader.onload = async (event) => {
                        logger.log(`${docInfo.title} loaded successfully`);
                        const result = event.target.result;

                        if (!result || typeof result !== 'string') {
                          logger.error('Invalid FileReader result');
                          alert('Failed to process image. Please try again.');
                          return;
                        }

                        const originalLen = result.length;
                        const compressed = await compressBase64Image(result, {
                          maxWidth: 1280, maxHeight: 1280, quality: 0.7, maxSizeKB: 200,
                        });
                        logger.log(`${docInfo.title} compressed: ${(originalLen/1024).toFixed(0)}KB → ${(compressed.length/1024).toFixed(0)}KB`);
                        form.setFieldValue(field_name, compressed);

                        setTimeout(() => {
                          form.setFieldTouched(field_name, true);
                        }, isIOS ? 300 : 100);
                      };

                      reader.onerror = (error) => {
                        logger.error(`Error reading ${docInfo.title}:`, error);
                        alert(`Failed to load image. Please try again.`);
                      };

                      try {
                        reader.readAsDataURL(file);
                      } catch (error) {
                        logger.error(`Error starting FileReader for ${docInfo.title}:`, error);
                        alert(`Failed to process image. Please try again.`);
                      }

                      // Reset input value so same file can be selected again
                      // Don't reset on iOS immediately as it can cause issues
                      setTimeout(() => {
                        e.target.value = '';
                      }, isIOS ? 500 : 0);
                    };

                    const handleDrop = (e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];

                      if (!file) {
                        logger.log('No file dropped');
                        return;
                      }

                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        alert('Please drop a valid image file (JPG, PNG, etc.)');
                        return;
                      }

                      // Validate file size (max 10MB)
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      if (file.size > maxSize) {
                        alert('Image size is too large. Please select an image smaller than 10MB.');
                        return;
                      }

                      logger.log(`Processing dropped ${docInfo.title}: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);

                      const reader = new FileReader();

                      reader.onload = async (event) => {
                        logger.log(`${docInfo.title} loaded successfully from drop`);
                        const compressed = await compressBase64Image(event.target.result, {
                          maxWidth: 1280, maxHeight: 1280, quality: 0.7, maxSizeKB: 200,
                        });
                        form.setFieldValue(field_name, compressed);
                        setTimeout(() => {
                          form.setFieldTouched(field_name, true);
                        }, 100);
                      };

                      reader.onerror = (error) => {
                        logger.error(`Error reading dropped ${docInfo.title}:`, error);
                        alert(`Failed to load image. Please try again.`);
                      };

                      reader.readAsDataURL(file);
                    };

                    const handleRemove = () => {
                      form.setFieldValue(field_name, '');
                    };

                    const hasFile = formikField.value && formikField.value.length > 0;
                    const inputId = `file-input-${field_name}`;

                    // Debug log
                    logger.log(`${docInfo.title} - Field: ${field_name}, hasFile: ${hasFile}, valueLength: ${formikField.value?.length || 0}`);

                    const openFilePicker = () => {
                      logger.log(`Opening file picker for ${docInfo.title}`);
                      const input = document.getElementById(inputId);
                      if (input) {
                        try {
                          // iOS Safari requires the input to be focused first
                          input.focus();
                          // Trigger click
                          input.click();
                          logger.log(`File picker opened successfully for ${docInfo.title}`);
                        } catch (error) {
                          logger.error(`Error opening file picker for ${docInfo.title}:`, error);
                        }
                      } else {
                        logger.error(`Input element not found: ${inputId}`);
                        alert(`Unable to open file picker. Please refresh the page and try again.`);
                      }
                    };

                    // DL Upload UI - same style as Insurance with dashed border
                    if (isDL) {
                      return (
                        <div
                          onClick={() => !hasFile && openFilePicker()}
                          onDrop={handleDrop}
                          onDragOver={(e) => e.preventDefault()}
                          style={{
                            border: `2px dashed ${hasFile ? docInfo.color : docInfo.borderColor}`,
                            borderRadius: '16px',
                            padding: '30px',
                            backgroundColor: hasFile ? '#fff' : docInfo.bgColor,
                            cursor: hasFile ? 'default' : 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            height: '320px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%'
                          }}
                        >
                          <input
                            type="file"
                            id={inputId}
                            onChange={handleFileChange}
                            accept="image/*"
                            style={{
                              position: 'absolute',
                              opacity: 0,
                              width: '1px',
                              height: '1px',
                              zIndex: -1
                            }}
                          />

                          {hasFile ? (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove();
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '12px',
                                  right: '12px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '36px',
                                  height: '36px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '20px',
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}
                              >
                                ×
                              </button>
                              <img
                                src={formikField.value}
                                alt={docInfo.title}
                                onError={(e) => {
                                  logger.error(`Failed to display ${docInfo.title} image`);
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  logger.log(`${docInfo.title} image displayed successfully`);
                                }}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '160px',
                                  objectFit: 'contain',
                                  borderRadius: '10px',
                                  marginBottom: '16px'
                                }}
                              />
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: docInfo.color,
                                fontWeight: '600',
                                fontSize: '16px'
                              }}>
                                <span style={{ fontSize: '22px' }}>✓</span>
                                {docInfo.title}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFilePicker();
                                }}
                                style={{
                                  marginTop: '14px',
                                  padding: '8px 24px',
                                  background: 'transparent',
                                  border: `2px solid ${docInfo.color}`,
                                  color: docInfo.color,
                                  borderRadius: '25px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '600'
                                }}
                              >
                                Change Image
                              </button>
                            </>
                          ) : (
                            <>
                              <div style={{
                                fontSize: '56px',
                                marginBottom: '16px'
                              }}>
                                {docInfo.icon}
                              </div>
                              <div style={{
                                fontWeight: '600',
                                fontSize: '18px',
                                color: '#1f2937',
                                marginBottom: '6px',
                                textAlign: 'center'
                              }}>
                                {docInfo.title}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                textAlign: 'center',
                                marginBottom: '16px'
                              }}>
                                Drag & drop or click to upload
                              </div>
                              <div style={{
                                padding: '10px 28px',
                                background: docInfo.color,
                                color: 'white',
                                borderRadius: '25px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}>
                                Choose File
                              </div>
                            </>
                          )}
                        </div>
                      );
                    }

                    // Insurance Upload UI - different style with dashed border
                    return (
                      <div
                        onClick={() => !hasFile && openFilePicker()}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        style={{
                          border: `2px dashed ${hasFile ? docInfo.color : docInfo.borderColor}`,
                          borderRadius: '16px',
                          padding: '30px',
                          backgroundColor: hasFile ? '#fff' : docInfo.bgColor,
                          cursor: hasFile ? 'default' : 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          height: '320px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%'
                        }}
                      >
                        <input
                          type="file"
                          id={inputId}
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: '1px',
                            height: '1px',
                            zIndex: -1
                          }}
                        />

                        {hasFile ? (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove();
                              }}
                              style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                              }}
                            >
                              ×
                            </button>
                            <img
                              src={formikField.value}
                              alt={docInfo.title}
                              onError={(e) => {
                                logger.error(`Failed to display ${docInfo.title} image`);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                logger.log(`${docInfo.title} image displayed successfully`);
                              }}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '160px',
                                objectFit: 'contain',
                                borderRadius: '10px',
                                marginBottom: '16px'
                              }}
                            />
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              color: docInfo.color,
                              fontWeight: '600',
                              fontSize: '16px'
                            }}>
                              <span style={{ fontSize: '22px' }}>✓</span>
                              {docInfo.title}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openFilePicker();
                              }}
                              style={{
                                marginTop: '14px',
                                padding: '8px 24px',
                                background: 'transparent',
                                border: `2px solid ${docInfo.color}`,
                                color: docInfo.color,
                                borderRadius: '25px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              Change Image
                            </button>
                          </>
                        ) : (
                          <>
                            <div style={{
                              fontSize: '56px',
                              marginBottom: '16px'
                            }}>
                              {docInfo.icon}
                            </div>
                            <div style={{
                              fontWeight: '600',
                              fontSize: '18px',
                              color: '#1f2937',
                              marginBottom: '6px',
                              textAlign: 'center'
                            }}>
                              {docInfo.title}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: '#6b7280',
                              textAlign: 'center',
                              marginBottom: '16px'
                            }}>
                              Drag & drop or click to upload
                            </div>
                            <div style={{
                              padding: '10px 28px',
                              background: docInfo.color,
                              color: 'white',
                              borderRadius: '25px',
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}>
                              Choose File
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }}
                </Field>
                <ErrorMessage name={field_name} component="div" className="error-message" />
              </div>
            );
          }

          // Original file/image handling for non-document uploads
          return (
            <div key={fieldKey} className="form-group file-upload-group">
              <Field name={field_name}>
                {() => (
                  <div className="file-upload-container">
                    {uploadedImage && (
                      <div className="image-preview existing-image">
                        {field.linkUrl ? (
                          <a
                            href={field.linkUrl}
                            target={field.linkTarget || "_blank"}
                            rel="noopener noreferrer"
                          >
                            <img
                              src={uploadedImage}
                              alt="Uploaded"
                              onError={(e) => {
                                logger.error('Failed to display existing uploaded image');
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                logger.log('Existing uploaded image displayed successfully');
                              }}
                              style={{
                                width: field.width ? `${field.width}${field.widthUnit || 'px'}` : '250px',
                                height: field.height ? `${field.height}${field.heightUnit || 'px'}` : '250px',
                                objectFit: 'contain',
                                maxWidth: '100%',
                                maxHeight: '100%',
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={uploadedImage}
                            alt="Uploaded"
                            onError={(e) => {
                              logger.error('Failed to display existing uploaded image');
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => {
                              logger.log('Existing uploaded image displayed successfully');
                            }}
                            style={{
                              width: field.width ? `${field.width}${field.widthUnit || 'px'}` : '250px',
                              height: field.height ? `${field.height}${field.heightUnit || 'px'}` : '250px',
                              objectFit: 'contain',
                              maxWidth: '100%',
                              maxHeight: '100%',
                            }}
                          />
                        )}
                      </div>
                    )}

                    {hasNewImagePreview(values[field_name], field) && (
                      <div className="image-preview new-image">
                        <p>New Image:</p>
                        <img
                          src={values[field_name]}
                          alt="Preview"
                          onError={(e) => {
                            logger.error('Failed to display new preview image');
                            e.target.style.display = 'none';
                          }}
                          onLoad={() => {
                            logger.log('New preview image displayed successfully');
                          }}
                          style={{
                            maxWidth: field.width ? `${field.width}${field.widthUnit || 'px'}` : '200px',
                            maxHeight: field.height ? `${field.height}${field.heightUnit || 'px'}` : '200px',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </Field>

              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
        }

        case 'textElement': {
          const textHtml = field.textContent || '';
          const hasTextLinks = textHtml.includes('<a ');
          if (hasTextLinks) {
            const textParts = [];
            const textLinkRegex = /<a\s+[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
            let textLastIdx = 0;
            let textMatch;
            while ((textMatch = textLinkRegex.exec(textHtml)) !== null) {
              if (textMatch.index > textLastIdx) {
                textParts.push(<span key={`te${textLastIdx}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textHtml.substring(textLastIdx, textMatch.index)) }} />);
              }
              textParts.push(
                <a key={`ta${textMatch.index}`} href={textMatch[1]} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#2486c5', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); }}
                >{textMatch[2].replace(/<[^>]*>/g, '')}</a>
              );
              textLastIdx = textMatch.index + textMatch[0].length;
            }
            if (textLastIdx < textHtml.length) {
              textParts.push(<span key={`te${textLastIdx}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textHtml.substring(textLastIdx)) }} />);
            }
            return <p style={{ ...field.textStyles, width: '100%' }}>{textParts}</p>;
          }
          return (
            <p
              style={{ ...field.textStyles, width: '100%' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textHtml) }}
            />
          );
        }

        case 'document_upload':
        case 'documentUpload':
          return (
            <div key={fieldKey} className="form-group" style={{ width: '100%' }}>
              <label className='label-txt' style={{ marginBottom: '12px', display: 'block' }}>
                {finalFieldLabel || 'Upload Documents'} {is_required && <span className="required">*</span>}
              </label>
              <Field name={field_name}>
                {({ form }) => (
                  <DocumentUpload
                    existingDocuments={form.values[field_name] || {}}
                    onDocumentsChange={(docs) => {
                      form.setFieldValue(field_name, docs);
                    }}
                  />
                )}
              </Field>
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );

        case 'authorized_contacts':
          const maxContacts = field.maxContacts || 4;
          const showAddButton = field.showAddButton !== false;
          const contactFields = field.contactFields || {};
          const roleChips = field.roleChips || {};
          const relationshipOptions = contactFields.relationship?.options || ['Parent', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Caregiver', 'Other'];

          return (
            <div key={fieldKey} className="form-group authorized-contacts-container" style={{ width: '100%' }}>
              <Field name={field_name}>
                {({ form, meta }) => {
                  // Ensure contacts array is always properly initialized
                  let contacts = form.values[field_name];

                  // Initialize contacts if not an array or empty
                  if (!Array.isArray(contacts) || contacts.length === 0) {
                    const initialContact = {
                      id: `contact_${Date.now()}_0`,
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      relationship: '',
                      otherRelationship: '',
                      isEmergencyContact: false,
                      isAuthorizedMedical: false,
                      _validated: false // Track if this contact has been validated
                    };
                    if (!form.values[field_name] || form.values[field_name].length === 0) {
                      setTimeout(() => form.setFieldValue(field_name, [initialContact]), 0);
                    }
                    contacts = [initialContact];
                  }

                  // Ensure each contact has an id
                  contacts = contacts.map((contact, idx) => ({
                    ...contact,
                    id: contact?.id || `contact_${Date.now()}_${idx}`
                  }));

                  // Parse validation errors
                  const validationError = meta.error;
                  const isTouched = meta.touched;

                  // Helper: Check if a field is required from admin config
                  const isFieldRequired = (fieldConfig) => {
                    if (!fieldConfig) return false;
                    return fieldConfig.required === true || fieldConfig.required === 'true' || fieldConfig.required === 1;
                  };

                  // Check if contact type selection is required from admin config
                  const isContactTypeRequired = roleChips.required === true || roleChips.required === 'true' || roleChips.required === 1;

                  // Helper: Check if a contact has ANY data entered (for conditional validation)
                  const contactHasAnyData = (contact) => {
                    return (
                      (contact.firstName && contact.firstName.trim() !== '') ||
                      (contact.lastName && contact.lastName.trim() !== '') ||
                      (contact.email && contact.email.trim() !== '') ||
                      (contact.phone && contact.phone.trim() !== '') ||
                      (contact.relationship && contact.relationship.trim() !== '') ||
                      (contact.otherRelationship && contact.otherRelationship.trim() !== '') ||
                      contact.isEmergencyContact === true ||
                      contact.isAuthorizedMedical === true
                    );
                  };

                  // When validation runs (touched + error), mark all contacts as validated
                  // This happens when user clicks Next and validation fails
                  if (isTouched && validationError) {
                    const hasUnvalidatedContacts = contacts.some(c => !c._validated);
                    if (hasUnvalidatedContacts) {
                      const updatedContacts = contacts.map(c => ({ ...c, _validated: true }));
                      setTimeout(() => form.setFieldValue(field_name, updatedContacts), 0);
                    }
                  }

                  // Helper: only show errors for contacts that have been validated AND have partial data
                  // This implements the "optional section" behavior - empty contacts can be skipped
                  const shouldShowErrorsFor = (contact) => {
                    return contact._validated === true && contactHasAnyData(contact);
                  };

                  const addContact = () => {
                    if (contacts.length < maxContacts) {
                      const newContact = {
                        id: `contact_${Date.now()}_${contacts.length}`,
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        relationship: '',
                        otherRelationship: '',
                        isEmergencyContact: false,
                        isAuthorizedMedical: false,
                        _validated: false // New contact - don't show errors until Next is clicked
                      };
                      form.setFieldValue(field_name, [...contacts, newContact]);
                    }
                  };

                  const removeContact = (contactIndex) => {
                    if (contacts.length > 1) {
                      const newContacts = contacts.filter((_, idx) => idx !== contactIndex);
                      form.setFieldValue(field_name, newContacts);
                    }
                  };

                  const updateContact = (contactIndex, fieldKey, value, additionalUpdates = {}) => {
                    const newContacts = contacts.map((contact, idx) => {
                      if (idx === contactIndex) {
                        return {
                          ...contact,
                          [fieldKey]: value,
                          ...additionalUpdates
                        };
                      }
                      return { ...contact };
                    });
                    form.setFieldValue(field_name, newContacts);
                  };

                  return (
                    <div className="authorized-contacts-wrapper">
                      {/* Optional Section Notice */}
                      <div className="optional-section-notice">
                        <span className="optional-badge">Optional</span>
                        <span className="optional-text">You may skip this section. If you enter any information, all required fields must be completed.</span>
                      </div>

                      {contacts.map((contact, contactIndex) => (
                        <div key={contact.id} className="contact-card">
                          {/* Contact Header */}
                          <div className="contact-header">
                            <h4 className="contact-title">Contact {contactIndex + 1}</h4>
                            {contactHasAnyData(contact) && (
                              <span className="contact-status-badge">In Progress</span>
                            )}
                            {contacts.length > 1 && (
                              <button
                                type="button"
                                className="remove-contact-btn"
                                onClick={() => removeContact(contactIndex)}
                                aria-label="Remove contact"
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {/* Contact Type Checkboxes */}
                          {(roleChips.emergencyContact?.enabled !== false || roleChips.authorizedMedical?.enabled !== false) && (
                            <div className="contact-type-section">
                              <div className="contact-type-header">
                                <span className="contact-type-label">
                                  Contact Type {isContactTypeRequired && contactHasAnyData(contact) && <span className="required">*</span>}
                                  {isContactTypeRequired && <span className="contact-type-hint">(Select at least one)</span>}
                                </span>
                                <button
                                  type="button"
                                  className="contact-type-help-btn"
                                  onClick={() => {
                                    const modal = document.getElementById(`help-modal-${contact.id}`);
                                    if (modal) modal.style.display = 'flex';
                                  }}
                                  aria-label="Help"
                                >
                                  <span>?</span> Help
                                </button>
                              </div>

                              {/* Help Modal */}
                              <div
                                id={`help-modal-${contact.id}`}
                                className="contact-type-help-modal"
                                onClick={(e) => {
                                  if (e.target.classList.contains('contact-type-help-modal')) {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              >
                                <div className="help-modal-content">
                                  <button
                                    type="button"
                                    className="help-modal-close"
                                    onClick={() => {
                                      const modal = document.getElementById(`help-modal-${contact.id}`);
                                      if (modal) modal.style.display = 'none';
                                    }}
                                    aria-label="Close"
                                  >
                                    ✕
                                  </button>
                                  <h3 className="help-modal-title">Contact Type Information</h3>
                                  <div className="help-modal-body">
                                    <div className="help-item">
                                      <div className="help-item-icon">🚨</div>
                                      <div className="help-item-content">
                                        <strong>Emergency Contact</strong>
                                        <p>The person we should notify immediately if you have a medical emergency while at our clinic. We will not discuss your general medical records or billing with this person.</p>
                                      </div>
                                    </div>
                                    <div className="help-item">
                                      <div className="help-item-icon">🏥</div>
                                      <div className="help-item-content">
                                        <strong>Authorized Medical Info Contact</strong>
                                        <p>The person you give {brandName} permission to speak with regarding your medical care, test results, and appointments.</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Standard Checkbox Options */}
                              <div className="contact-type-options">
                                {roleChips.emergencyContact?.enabled !== false && (
                                  <label className={`contact-type-checkbox ${contact.isEmergencyContact ? 'checked' : ''} ${isContactTypeRequired && shouldShowErrorsFor(contact) && !contact.isEmergencyContact && !contact.isAuthorizedMedical ? 'error' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={contact.isEmergencyContact || false}
                                      onChange={(e) => updateContact(contactIndex, 'isEmergencyContact', e.target.checked)}
                                    />
                                    <span className="checkbox-custom"></span>
                                    <span className="checkbox-icon">🚨</span>
                                    <span className="checkbox-label">Emergency Contact</span>
                                  </label>
                                )}
                                {roleChips.authorizedMedical?.enabled !== false && (
                                  <label className={`contact-type-checkbox ${contact.isAuthorizedMedical ? 'checked' : ''} ${isContactTypeRequired && shouldShowErrorsFor(contact) && !contact.isEmergencyContact && !contact.isAuthorizedMedical ? 'error' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={contact.isAuthorizedMedical || false}
                                      onChange={(e) => updateContact(contactIndex, 'isAuthorizedMedical', e.target.checked)}
                                    />
                                    <span className="checkbox-custom"></span>
                                    <span className="checkbox-icon">🏥</span>
                                    <span className="checkbox-label">Authorized Medical Info</span>
                                  </label>
                                )}
                              </div>

                              {/* Validation Error - only if admin marked as required */}
                              {isContactTypeRequired && shouldShowErrorsFor(contact) && !contact.isEmergencyContact && !contact.isAuthorizedMedical && (
                                <span className="field-error" style={{ marginTop: '8px', display: 'block' }}>
                                  Please select at least one contact type
                                </span>
                              )}
                            </div>
                          )}

                          {/* Contact Fields */}
                          <div className="contact-fields-grid">
                            {contactFields.firstName?.enabled !== false && (
                              <div className="contact-field">
                                <label>
                                  {contactFields.firstName?.label || 'First Name'}
                                  {isFieldRequired(contactFields.firstName) && contactHasAnyData(contact) && <span className="required">*</span>}
                                </label>
                                <input
                                  type="text"
                                  value={contact.firstName || ''}
                                  onChange={(e) => updateContact(contactIndex, 'firstName', e.target.value)}
                                  placeholder={contactFields.firstName?.placeholder || 'First Name'}
                                  className={`form-control ${isFieldRequired(contactFields.firstName) && shouldShowErrorsFor(contact) && !contact.firstName ? 'error-input' : ''}`}
                                />
                                {isFieldRequired(contactFields.firstName) && shouldShowErrorsFor(contact) && !contact.firstName && (
                                  <span className="field-error">First Name is required</span>
                                )}
                              </div>
                            )}

                            {contactFields.lastName?.enabled !== false && (
                              <div className="contact-field">
                                <label>
                                  {contactFields.lastName?.label || 'Last Name'}
                                  {isFieldRequired(contactFields.lastName) && contactHasAnyData(contact) && <span className="required">*</span>}
                                </label>
                                <input
                                  type="text"
                                  value={contact.lastName || ''}
                                  onChange={(e) => updateContact(contactIndex, 'lastName', e.target.value)}
                                  placeholder={contactFields.lastName?.placeholder || 'Last Name'}
                                  className={`form-control ${isFieldRequired(contactFields.lastName) && shouldShowErrorsFor(contact) && !contact.lastName ? 'error-input' : ''}`}
                                />
                                {isFieldRequired(contactFields.lastName) && shouldShowErrorsFor(contact) && !contact.lastName && (
                                  <span className="field-error">Last Name is required</span>
                                )}
                              </div>
                            )}

                            {contactFields.email?.enabled !== false && (
                              <div className="contact-field">
                                <label>
                                  {contactFields.email?.label || 'Email Address'}
                                  {isFieldRequired(contactFields.email) && contactHasAnyData(contact) && <span className="required">*</span>}
                                </label>
                                <input
                                  type="email"
                                  value={contact.email || ''}
                                  onChange={(e) => updateContact(contactIndex, 'email', e.target.value)}
                                  placeholder={contactFields.email?.placeholder || 'Email Address'}
                                  className={`form-control ${isFieldRequired(contactFields.email) && shouldShowErrorsFor(contact) && !contact.email ? 'error-input' : ''}`}
                                />
                                {isFieldRequired(contactFields.email) && shouldShowErrorsFor(contact) && !contact.email && (
                                  <span className="field-error">Email is required</span>
                                )}
                              </div>
                            )}

                            {contactFields.phone?.enabled !== false && (
                              <div className="contact-field">
                                <label>
                                  {contactFields.phone?.label || 'Phone Number'}
                                  {isFieldRequired(contactFields.phone) && contactHasAnyData(contact) && <span className="required">*</span>}
                                </label>
                                <input
                                  type="tel"
                                  value={contact.phone || ''}
                                  onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    updateContact(contactIndex, 'phone', numericValue);
                                  }}
                                  placeholder={contactFields.phone?.placeholder || 'Phone Number'}
                                  className={`form-control ${isFieldRequired(contactFields.phone) && shouldShowErrorsFor(contact) && (!contact.phone || contact.phone.length !== 10) ? 'error-input' : ''}`}
                                  maxLength={10}
                                />
                                {isFieldRequired(contactFields.phone) && shouldShowErrorsFor(contact) && !contact.phone && (
                                  <span className="field-error">Phone is required</span>
                                )}
                                {isFieldRequired(contactFields.phone) && shouldShowErrorsFor(contact) && contact.phone && contact.phone.length !== 10 && (
                                  <span className="field-error">Phone must be 10 digits</span>
                                )}
                              </div>
                            )}

                            {contactFields.relationship?.enabled !== false && (
                              <div className="contact-field relationship-field">
                                <label>
                                  {contactFields.relationship?.label || 'Relationship'}
                                  {isFieldRequired(contactFields.relationship) && contactHasAnyData(contact) && <span className="required">*</span>}
                                </label>
                                <select
                                  value={contact.relationship || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const additionalUpdates = newValue !== 'Other' ? { otherRelationship: '' } : {};
                                    updateContact(contactIndex, 'relationship', newValue, additionalUpdates);
                                  }}
                                  className={`form-control ${isFieldRequired(contactFields.relationship) && shouldShowErrorsFor(contact) && !contact.relationship ? 'error-input' : ''}`}
                                >
                                  <option value="">Select Relationship</option>
                                  {relationshipOptions.map((option, idx) => (
                                    <option key={idx} value={option}>{option}</option>
                                  ))}
                                </select>
                                {isFieldRequired(contactFields.relationship) && shouldShowErrorsFor(contact) && !contact.relationship && (
                                  <span className="field-error">Relationship is required</span>
                                )}

                                {/* Other Relationship Input */}
                                {contact.relationship === 'Other' && (
                                  <input
                                    type="text"
                                    value={contact.otherRelationship || ''}
                                    onChange={(e) => updateContact(contactIndex, 'otherRelationship', e.target.value)}
                                    placeholder="Please specify relationship"
                                    className="form-control other-relationship-input"
                                    style={{ marginTop: '8px' }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Add Another Contact Button */}
                      {showAddButton && contacts.length < maxContacts && (
                        <button
                          type="button"
                          className="add-contact-btn"
                          onClick={addContact}
                        >
                          + Add Another Contact ({maxContacts - contacts.length} slots remaining)
                        </button>
                      )}

                    </div>
                  );
                }}
              </Field>
            </div>
          );

        default:
          return (
            <div key={fieldKey} className="form-group">
              <label htmlFor={field_name} className='label-txt'>
                {finalFieldLabel || field_name} {is_required && <span className="required">*</span>}
              </label>
              <Field
                type="text"
                name={field_name}
                id={field_name}
                placeholder={field_placeholder || ''}
                className={`form-control ${errors[field_name] && touched[field_name] ? 'error' : ''}`}
              />
              <ErrorMessage name={field_name} component="div" className="error-message" />
            </div>
          );
      }
    } catch (error) {
      logger.error('Error rendering field:', field, error);
      return (
        <div key={fieldKey} className="form-group error-field">
          <p>Error rendering field: {field_name || 'Unknown field'}</p>
        </div>
      );
    }
  };
 
  if (!step || !moduleName) {
    return (
      <div className="dynamic-step-container">
        <div className="error-message">
          <p>Invalid step configuration</p>
        </div>
      </div>
    );
  }

  // Helper function to update ROS field with conditional values
const updateROSFieldWithConditionals = (form, fieldName, optionValue, conditionalInputs, optionIndex) => {
  const currentValues = Array.isArray(form.values[fieldName]) ? [...form.values[fieldName]] : [];

  // Get all selected conditional fields for this option
  const selectedConditionals = conditionalInputs
    .map((conditionalField, index) => {
      const conditionalFieldName = `${fieldName}_conditional_${optionIndex}_${index}`;
      return form.values[conditionalFieldName] === true ? conditionalField.label : null;
    })
    .filter(label => label !== null);

  // Remove existing entries for this option
  const filteredValues = currentValues.filter(val => 
    val !== optionValue && !val.startsWith(`${optionValue} - `)
  );

  // Add new formatted value
  if (selectedConditionals.length > 0) {
    const formattedValue = `${optionValue} - ${selectedConditionals.join(', ')}`;
    form.setFieldValue(fieldName, [...filteredValues, formattedValue]);
  } else {
    // If no conditionals selected, just add the main option
    form.setFieldValue(fieldName, [...filteredValues, optionValue]);
  }
};

  return (
    <div className={`dynamic-step-container ${isAuthorizedContacts ? 'authorized-contacts' : ''}`}>
      <div className="step-header">
        <h2 className="step-title">
          {(moduleData && moduleData.display_name) || moduleName}
        </h2>
        {moduleData && moduleData.description && (
          <p className="step-description">{String(moduleData.description)}</p>
        )}
      </div>

      {errorDisplay}

      <div
        className={`fields-container ${isAuthorizedContacts ? 'authorized-contacts-grid' : ''}`}
        style={isAuthorizedContacts ? { gap: '20px' } : { display: 'flex', flexWrap: 'wrap', gap: '20px' }}
      >
        {moduleFields && Array.isArray(moduleFields) && moduleFields.length > 0 ? (
          moduleFields.map((field, index) => renderField(field, index)).filter(Boolean)
        ) : (
          <div className="no-fields-message">
            <p>No fields available for this step.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicStepComponent;
